import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Radio,
  TreeSelect,
  Button,
  Collapse,
  message,
  Segmented,
  Tag,
  Tooltip,
  Switch,
  Alert,
  DatePicker,
  Space,
} from 'antd';
import {
  getTaskTypes,
  getSceneTree,
  getSceneTagTree,
  getSceneInspectionList,
  resolveSceneIdsByNames as resolveSceneIdsByNamesApi,
  getSceneGroupStats,
  createJob,
  createBazelJob,
  getJobBazelPresets,
  getDailyTagsExisting,
  getDailyVersionsByTag,
  getJobPredictionDefaultVersion,
  createSchedule,
  previewCron,
  getPriorityList,
  BazelPresets,
  TaskType,
  SceneTreeNode,
  SceneTagNode, tagDisplayName,
  SceneGroupStat,
  BuildType,
  BuildSpec,
  CreateJobParams,
  CreateBazelJobParams,
  DailyVersionItem,
  JobItem,
  ScheduleDelayUnit,
  PriorityOption,
} from '@apis/simulation';
import {
  encodeGroupBackendToken, sceneGroupDisplay,
} from '@/constants/sceneGroup';
import dayjs, { type Dayjs } from 'dayjs';
import { usePackageExistsProbe } from './usePackageExistsProbe';
import CustomParametersFields from './CustomParametersFields';
import ModelReleaseField from './ModelReleaseField';
import PredictionDataVersionPicker from './PredictionDataVersionPicker';
import { useCustomParameterDefinitions } from './useCustomParameterDefinitions';
import {
  splitModelReleaseParams, mergeModelReleaseParams, PROMOTED_CUSTOM_PARAMETER_KEYS,
} from '@/utils/jobCustomParameters';
import { detectAlgoVersionPreset, extractPredictionDataVersion } from '@/utils/versionTag';
import { renderProbeHint, renderProbeSuffix } from './probeIndicators';
import DailyBaseVersionPicker, {
  DAILY_TAG_HINT_EVENT,
} from './DailyBaseVersionPicker';

// 前端 UI 类型 3 项:
// - precompiled(合并原「导包」+「版本号」)→ 后端 package_neodata
// - package_bazel → 后端 package_bazel(手工输入 baseVersion + branchDpInfo)
// - daily → 后端 package_bazel(基于 daily 历史打版选一个 baseVersion,快捷方式)
// compile_platform / cmake 编译已废弃,不再在 UI 出现;后端 CompileBuildSpec 保留
// 供老 Job 详情回读。详见 docs/daily-tag-and-build-option-refactor.md §4.1。
type UiBuildType = 'precompiled' | 'package_bazel' | 'daily';

// 默认选中「使用编译好的版本」
const DEFAULT_BUILD_TYPE: UiBuildType = 'precompiled';
const BUILD_TYPE_LABELS: Record<UiBuildType, ReactNode> = {
  precompiled: '使用编译好的版本',
  package_bazel: '基于已有版本编译',
  daily: (
    <>
      基于 daily 版本编译<strong>(推荐)</strong>
    </>
  ),
};
const BUILD_TYPE_TOOLTIPS: Record<UiBuildType, string> = {
  precompiled: '直接使用已发布的算法包版本号或百度云 S3 包地址，无需重新编译。',
  package_bazel: '基于一个已有的基线版本 + 分支/commit 组合，走 cicd 平台重新编译一份产物。',
  daily: '选择一个 daily 通道 tag(如 release.10.11 / orin_dev),从近期 daily 打版历史里挑一个基线版本重跑;提交后与「基于已有版本编译」走同一 bazel 编译链路。',
};
const BUILD_TYPE_ORDER: UiBuildType[] = [
  'precompiled',
  'daily',
  'package_bazel',
];

// precompiled 子选项卡：
// - version:直接输版本号(短串,走后端模板)
// - s3:bos:// 完整路径透传
// - daily:选一个 daily tag → 从近期打版历史里挑一个 versionNo 回填,再走 version 分支
//   (即最终仍以 algorithmVersion 字段提交,走 package_neodata;与 buildType='daily'
//   分支的区别:那个是拉基线后走 bazel 重编,这个是直接拿 daily 那天已经打好的 neodata 包)
type PrecompiledSubtype = 'version' | 's3' | 'daily';
const PRECOMPILED_SUBTYPE_LABELS: Record<PrecompiledSubtype, string> = {
  version: '版本号',
  s3: 'S3 地址',
  daily: '使用 daily 版本',
};

// ── bazel 一键填版本预设 ──
// 预设 JSON 由后端 ``/job/bazel-presets`` 返回（管理员在「系统 - 系统参数运维」页
// 编辑）；前端挂载时拉取，不再硬编码。结构类型 BazelPreset / BazelPresets 在
// @apis/simulation 统一定义，本文件只 import BazelPresets 用。

/**
 * 检测算法包版本是否命中任一预设 key。实现统一收敛到 `@/utils/versionTag`
 * （与 CreateSceneQcJobModal 共用，避免手抄拷贝）。
 */

// 「按场景 id 发起 job」单次最多携带的场景数（与后端 MAX_SCENE_IDS_PER_JOB 一致）。
export const MAX_SCENE_IDS_PER_JOB = 100;

// job.job_name 列为 varchar(64)，复制命名需与后端上限保持一致
const MAX_JOB_NAME_LEN = 64;

/**
 * 生成「复制」后的 Job 名称：把末尾连续的 `_copy` / `_copyN` 归一成单个带序号后缀，
 * 并截断 base 段保证不超过 {@link MAX_JOB_NAME_LEN}。
 */
function buildCopyName(name: string): string {
  if (!name) return '';
  const m = name.match(/^(.*?)((?:_copy\d*)+)$/);
  const base = m ? m[1] : name;
  let next = 1;
  if (m) {
    const last = m[2].match(/_copy(\d*)$/);
    next = last && last[1] ? parseInt(last[1], 10) + 1 : 2;
  }
  const suffix = next <= 1 ? '_copy' : `_copy${next}`;
  const maxBase = Math.max(0, MAX_JOB_NAME_LEN - suffix.length);
  const trimmedBase = base.length > maxBase ? base.slice(0, maxBase) : base;
  return `${trimmedBase}${suffix}`;
}

/** UI 端 module/branch 分隔符：兼容空格（首选）与冒号（兼容老格式）。 */
const BRANCH_DP_PAIR_SEP_RE = /[:\s]+/;
/** UI 端多对之间的分隔符：换行（首选）或英文逗号。 */
const BRANCH_DP_GROUP_SEP_RE = /[,\n]+/;

/** 把单行 ``module<sep>branch`` 拆成 ``[module, branch]``；不符合格式返回 null。 */
function parseBranchDpEntry(line: string): [string, string] | null {
  const trimmed = (line || '').trim();
  if (!trimmed) return null;
  const m = trimmed.split(BRANCH_DP_PAIR_SEP_RE);
  if (m.length < 2) return null;
  const module = m[0]?.trim();
  const branch = m.slice(1).join(' ').trim();
  if (!module || !branch) return null;
  return [module, branch];
}

/** 把 UI 多行输入归一化为后端接口契约的 ``module:branch,module2:branch2``。 */
function normalizeBranchDpInfoForApi(raw: string): string {
  return (raw || '')
    .split(BRANCH_DP_GROUP_SEP_RE)
    .map(parseBranchDpEntry)
    .filter((p): p is [string, string] => p !== null)
    .map(([module, branch]) => `${module}:${branch}`)
    .join(',');
}

/** 从 UI 输入提取模块名列表（用于 changeModule 自动填充）。 */
function deriveChangeModule(branchDpInfo: string): string {
  const modules: string[] = [];
  for (const entry of (branchDpInfo || '').split(BRANCH_DP_GROUP_SEP_RE)) {
    const pair = parseBranchDpEntry(entry);
    const mod = pair?.[0];
    if (mod && !modules.includes(mod)) {
      modules.push(mod);
    }
  }
  return modules.join(',');
}

/**
 * 合并「当前已填分支」与「一键填版本预设分支」，输出 ``module:branch,...`` 串。
 *
 * 规则（保序）：以当前已填条目为基准并保持其顺序，预设里**同名仓库覆盖**其分支、
 * **新仓库追加**到末尾；当前已填但预设未涉及的仓库**原样保留**。current 为空时结果
 * 等于预设本身（回退到旧「直接覆盖」行为）。
 */
function mergeBranchDpInfo(current: string, presetBranchDpInfo: string): string {
  const entries: [string, string][] = [];
  const posByModule = new Map<string, number>();
  const upsert = (raw: string) => {
    for (const line of (raw || '').split(BRANCH_DP_GROUP_SEP_RE)) {
      const pair = parseBranchDpEntry(line);
      if (!pair) continue;
      const [module, branch] = pair;
      const at = posByModule.get(module);
      if (at !== undefined) {
        entries[at] = [module, branch]; // 覆盖（同名去重 / 预设覆盖已填）
      } else {
        posByModule.set(module, entries.length);
        entries.push([module, branch]);
      }
    }
  };
  upsert(current);              // 先铺当前已填，确立顺序
  upsert(presetBranchDpInfo);   // 预设覆盖同名、追加新仓库
  // 用换行拼接、仓库与分支用空格间隔：UI 上一个仓库一行更易读
  // （parseBranchDpEntry 兼容空格/冒号，提交时 normalizeBranchDpInfoForApi 再统一成逗号串）。
  return entries.map(([m, b]) => `${m} ${b}`).join('\n');
}

const DEFAULT_BRANCH = 'orin_dev';

/** buildCommitIdString 的逆操作：把 ``repo:val,repo:val`` 还原为表单 textarea 文本。 */
function commitIdToTextarea(commitId: string | null | undefined): string {
  if (!commitId) return '';
  const lines: string[] = [];
  for (const pair of commitId.split(',')) {
    const [repo, value] = pair.split(':').map(x => x.trim());
    if (repo && value && value !== DEFAULT_BRANCH) {
      lines.push(`${repo} ${value}`);
    }
  }
  return lines.join('\n');
}

/** cmake 编译已废弃(2026-08-26 详见 daily-tag-and-build-option-refactor.md):
 *  UI 已删除 compile_platform Radio,该函数已无 caller。删除函数体避免死代码,
 *  compile_platform BuildSpec 仍在后端 schema 里保留供老 Job 详情回读。 */
// (无实现)

interface CopyDefaults {
  algorithmVersion: string;
  /** 后端 snapshot 后的完整包路径（package_neodata 模板拼好 / bos:// 透传）。
   *  Radio 手动切到「导包」时取这个完整 URL 填 s3PackageUrl。 */
  fullPackageUrl: string;
  branchCommitInput: string;
  baseVersion?: string;
  versionDescription?: string;
  changeModule?: string;
  branchDpInfo?: string;
}

export interface PresetScene {
  id: number;
  name: string;
}

export interface CreateJobModalProps {
  open: boolean;
  onClose: () => void;
  /** 创建成功后回调（如刷新列表）。 */
  onSuccess?: () => void;
  /**
   * 场景管理「发起任务」预选场景：调用方已持有 ``{id, name}``，弹窗内直接预填到
   * 「按场景」页签，**不再走 id→name 反查接口**。提交时使用 id 直发，上限
   * {@link MAX_SCENE_IDS_PER_JOB}。
   */
  presetScenes?: PresetScene[];
  /**
   * 「按任务查询」入口:JobTaskList 页 FilteredStatRow 的「基于当前查询发起任务」
   * 按钮 → confirm 弹窗 → 打开 CreateJobModal 时传入。场景池锁定为该 Job 里
   * 满足 filters 组合的 distinct scene_id。用户在弹窗内**不可**再改 filter,只
   * 能配置版本/参数;想改 filter 需回 TaskList 页重新查询。
   *
   * 无该 prop 时,Segmented 里不显示「按任务查询」页签。
   */
  byTaskQuery?: {
    jobId: number;
    /** TaskListParams 里除 jobId/current/pageSize 外的全部 filter,可空对象。
     *  作为 submit 时透传给后端的 payload;弹窗内不再展开成表单字段。 */
    filters: Record<string, unknown>;
    /** 已格式化的 filter 展示项(中文名 + 中文值),给「按任务查询」页签只读渲染。
     *  由 JobTaskList 页调用方用 filterLabels.extractNonEmptyFilters 生成好后传入。 */
    filterEntries?: Array<{ key: string; label: string; valueLabel: string }>;
    /** 只读展示用:该 Job 的名称(可选,能显示更友好)。 */
    jobName?: string;
    /** 只读展示用:场景池大小(distinct scene_id 数)。 */
    sceneCount?: number;
  };
  /**
   * 场景管理「勾选整个集合发起任务」：调用方传入 scene_tree 节点 id 数组（叶子或目录），
   * 或分组虚拟节点编码字符串 ``L<leafId>G:<groupKey>``（daily 分组勾选场景）。
   * 弹窗打开时预填到「按场景集合」页签：
   * - number 项 → 走 TreeSelect 正常显示
   * - string 项 → 顶部单独渲染只读 chip（TreeSelect 无对应节点无法回显）
   * 提交时两类合并拼进 sceneSetIds 逗号串，后端 job_service._parse_scene_set_tokens
   * 解析出 (parent_ids, scene_groups_by_leaf) 走「按场景集」原有链路，不受 100 上限。
   */
  presetSceneSetIds?: (number | string)[];
  /**
   * 分组虚拟节点显示名映射 ``{"L91G:detour_problem": "daily / 绕行问题"}``；
   * 仅用于顶部只读 chip 展示，方便用户核对来源。
   */
  presetSceneSetGroupLabels?: Record<string, string>;
  /**
   * 场景管理「标签检索」选中标签后点「发起任务」：传入标签树节点 id 数组（标签叶子或目录，
   * 目录由后端展开为其全部后代叶子标签）。弹窗打开时自动切到「按场景标签」页签
   * 并只读展示所选标签。**不传该 prop 时「按场景标签」页签依然可用**，用户在弹窗内
   * 通过标签树自选（如感知仿真任务列表直接创建 Job）。提交时拼进 sceneTagIds 逗号串，
   * 后端按标签实时展开为场景并集，不受 100 上限。
   */
  presetSceneTagIds?: number[];
  /**
   * 标签显示名映射（tag id → 全路径名，如 `31 → "/业务/感知/TLD/绿红"`），
   * 仅供「按场景标签」页签只读 chip 展示，方便用户核对来源。
   */
  presetSceneTagLabels?: Record<number, string>;
  /** 复制 job：传入原 record，弹窗打开时自动回填发起参数。 */
  copyRecord?: JobItem | null;
  /**
   * dry-run 模式:不真调 create_job / create_bazel_job,只把弹窗组装出的 payload
   * 通过 ``onDryRunResult`` 回调返给上层(打版触发规则用来生成模板 JSON)。
   * 打开时上层可传 ``versionPlaceholder`` (默认 ``"{version}"``),提交时会把 buildSpec.algorithmVersion
   * (按包) 或 baseVersion (按 bazel) 替换成该占位符;上层可以直接把 JSON 塞进 jobParams
   * 输入框,后端消费 kafka 时再把 {version} 还原成真 version。
   */
  dryRunMode?: boolean;
  versionPlaceholder?: string;
  onDryRunResult?: (payload: {
    endpoint: 'create' | 'bazel_create';
    params: Record<string, unknown>;
  }) => void;
  /**
   * 感知数据刷写（PE_DATA）页专用：任务类型锁定为该类型（下拉禁用、不拉通用列表），
   * 模型主版本必填（DAG 不写死检测模型），并多一个可选「版本 tag」输入（提交为 perceptionTag）。
   * 不传时弹窗行为不变。
   */
  perceptionRefreshTaskType?: { id: number; name: string };
}

/** 「基于 daily 版本」radio 下的 Tag 下拉联动区。
 *
 *  交互:
 *  - Tag 下拉:拉 GET /job/daily-tags-existing 拿实际使用过的 tag(倒序按最近使用)
 *  - 选完 Tag → GET /job/daily/versions?tag=&limit=200 拉该 tag 下近 200 条 daily job
 *  - **自动回填**最新一条 versionNo 到父表单的「基线版本」;把候选历史列表通过
 *    onVersionsLoaded 上抛给父层,供下方「基线版本」Picker 的历史弹窗做搜索/选择
 *
 *  注:回填的是 daily 命名器输出的 versionNo(如 20260826.daily.sim.orin_dev),
 *  不是 DailyVersionItem.baseVersion(那是当初创建该 job 时用户传的原始基线,例
 *  x86.t20250531.dev.299,不能作为再次触发 daily 的 --base-version)。
 *
 *  详见 docs/daily-tag-and-build-option-refactor.md §4.1 / §4.5。
 */
function DailyTagPicker(props: {
  onPick: (baseVersion: string) => void;
  onVersionsLoaded: (versions: DailyVersionItem[]) => void;
  onTagChange: (tag: string | undefined) => void;
}) {
  const { onPick, onVersionsLoaded, onTagChange } = props;
  const [tags, setTags] = useState<string[]>([]);
  const [tagLoading, setTagLoading] = useState(false);
  const [selectedTag, setSelectedTag] = useState<string | undefined>(undefined);
  const [versionLoading, setVersionLoading] = useState(false);
  // 用户点了未激活的「历史」按钮时,Picker 广播事件让本组件把 Select 高亮一下。
  // 是一个 tick 计数,每次 event 触发 +1,useEffect 里根据它启动一次性动画。
  const [hintTick, setHintTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setTagLoading(true);
    getDailyTagsExisting()
      .then((list) => {
        if (!cancelled) setTags(list);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setTagLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // 监听 DailyBaseVersionPicker 广播的引导事件:未选 tag 时点了「历史」,本组件把 Select 高亮 ~1.2s。
  useEffect(() => {
    const onHint = () => setHintTick((n) => n + 1);
    window.addEventListener(DAILY_TAG_HINT_EVENT, onHint);
    return () => window.removeEventListener(DAILY_TAG_HINT_EVENT, onHint);
  }, []);

  const handleTagSelect = useCallback((tag: string) => {
    setSelectedTag(tag);
    onTagChange(tag);
    setVersionLoading(true);
    getDailyVersionsByTag(tag, 200)
      .then((rows) => {
        // stale-guard:请求发出后用户可能 clear / 换 tag,响应回来时该丢弃。
        // 用最新的 selectedTag 校验;函数闭包里的 tag 是本次请求发起时的 tag。
        setSelectedTag((current) => {
          if (current !== tag) return current;
          onVersionsLoaded(rows);
          const latest = rows.find((r) => !!r.versionNo);
          if (latest?.versionNo) {
            onPick(latest.versionNo);
          }
          return current;
        });
      })
      .catch(() => {
        onVersionsLoaded([]);
      })
      .finally(() => {
        setVersionLoading(false);
      });
  }, [onPick, onVersionsLoaded, onTagChange]);

  return (
    <Form.Item
      label={
        <span
          className={hintTick > 0 ? 'daily-tag-label-hint' : undefined}
          key={hintTick}
        >
          Daily Tag
        </span>
      }
      tooltip="选择一个 daily 通道 tag(如 release.10.11 / orin_dev),后端会自动把该 tag 下最新一条 daily 打版的版本号(versionNo)回填到下方「基线版本」;同时下方输入框变成可搜索,能从近 200 条历史 versionNo 里挑其它版本。"
      extra={
        versionLoading
          ? <span style={{ color: '#9b9da3', fontSize: 12 }}>加载 tag 下历史版本中...</span>
          : selectedTag
            ? <span style={{ color: '#9b9da3', fontSize: 12 }}>
                已回填该 tag 下最新 daily 版本号;下方「基线版本」支持搜索历史或手动输入
              </span>
            : undefined
      }
    >
      <span
        className={hintTick > 0 ? 'daily-tag-select-pulse' : undefined}
        // key 换掉让 CSS 动画重跑;单次触发靠一次性 remount。
        key={hintTick}
        style={{ display: 'inline-block', borderRadius: 6 }}
      >
        <Select
          value={selectedTag}
          loading={tagLoading}
          placeholder="选择 daily 通道 tag"
          showSearch
          allowClear
          style={{ width: 240 }}
          options={tags.map((t) => ({ label: t, value: t }))}
          onSelect={(v) => handleTagSelect(v as string)}
          // onChange 兜底:allowClear × / 键盘清空等所有路径都会触发 onChange(undefined)。
          // 只在真变成 undefined 时做清理,防止和 onSelect 重复触发选中副作用。
          onChange={(v) => {
            if (v === undefined || v === null || v === '') {
              setSelectedTag(undefined);
              onVersionsLoaded([]);
              onTagChange(undefined);
            }
          }}
        />
      </span>

      {/* 引导动画:CSS-only。ring 从内向外扩散,同时 label 短暂变蓝。 */}
      <style>{`
        @keyframes daily-tag-hint-ring {
          0%   { box-shadow: 0 0 0 0 rgba(22, 119, 255, 0.55); }
          70%  { box-shadow: 0 0 0 10px rgba(22, 119, 255, 0); }
          100% { box-shadow: 0 0 0 0 rgba(22, 119, 255, 0); }
        }
        @keyframes daily-tag-hint-label {
          0%   { color: #1677ff; }
          70%  { color: #1677ff; }
          100% { color: inherit; }
        }
        .daily-tag-select-pulse {
          animation: daily-tag-hint-ring 1.2s ease-out 2;
        }
        .daily-tag-label-hint {
          animation: daily-tag-hint-label 2.4s ease-out 1;
          font-weight: 600;
        }
      `}</style>
    </Form.Item>
  );
}

/**
 * 创建 Job 弹窗（JobList「创建 Job」/「复制」与场景管理「发起任务」共用）。
 *
 * 场景来源支持三种：
 * - 「按场景集合」TreeSelect；
 * - 「按场景」输入场景名；
 * - 「按场景标签」：无 presetSceneTagIds 时（如感知仿真任务列表直接创建）在弹窗内
 *   自选标签树（可多选、目录自动展开）；有 presetSceneTagIds 时（场景管理「发起任务」）
 *   标签池锁定、只读展示。
 *
 * 默认（JobList「创建 Job」）：默认页签「按场景集合」；
 * - 复制 job：按原 job 的 sceneSource 切到对应页签并回填（按场景时反查场景名）；
 * - 场景管理「发起任务」：传 {@link CreateJobModalProps.presetScenes}，
 *   自动切到「按场景」页签并直接填入 id+name，不调接口反查。
 */
export default function CreateJobModal({
  open,
  onClose,
  onSuccess,
  presetScenes,
  byTaskQuery,
  presetSceneSetIds,
  presetSceneSetGroupLabels,
  presetSceneTagIds,
  presetSceneTagLabels,
  copyRecord = null,
  dryRunMode = false,
  versionPlaceholder = '{version}',
  onDryRunResult,
  perceptionRefreshTaskType,
}: CreateJobModalProps) {
  const [createForm] = Form.useForm();
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
  const [sceneTree, setSceneTree] = useState<SceneTreeNode[]>([]);
  /**
   * 「按场景标签」页签的标签树。无 presetSceneTagIds 时（感知/PNC 任务列表直接创建）
   * 在弹窗内按需懒加载一次，供用户多选标签（含目录，后端展开为后代叶子并集）。
   */
  const [tagTree, setTagTree] = useState<SceneTagNode[]>([]);
  const [tagTreeLoading, setTagTreeLoading] = useState(false);
  const tagTreeRequestedRef = useRef(false);
  /**
   * daily 子树叶子节点的 scene_group 分组统计（弹窗打开时懒加载一次）：
   * `{ [leafId]: [{sceneGroup, count}] }`。用来在「按场景集合」TreeSelect
   * 的 daily 叶子下动态挂出「绕行问题 / 变道问题 ...」虚拟子节点，
   * value = 后端 token 字符串 `L<leafId>G:<groupKey>`，提交时进 sceneSetIds 逗号串。
   */
  const [dailyGroupStats, setDailyGroupStats] = useState<Record<number, SceneGroupStat[]>>({});
  const [createLoading, setCreateLoading] = useState(false);
  const [buildType, setBuildType] = useState<UiBuildType>(DEFAULT_BUILD_TYPE);
  // precompiled 内部子选项卡：version=版本号 / s3=S3 地址；两条都映射到后端 package_neodata。
  const [precompiledSubtype, setPrecompiledSubtype] = useState<PrecompiledSubtype>('version');
  // bazel 一键填版本 下拉当前选择：'custom' 表示自定义（不自动填），其它值为预设 key。
  // key 来自后端 /job/bazel-presets 返回的字典，运行时动态；不再硬编码 '10.10'/'10.9'。
  const [bazelPresetChoice, setBazelPresetChoice] = useState<'custom' | string>('custom');
  // bazel 预设字典：挂载时从 /job/bazel-presets 拉取；拉失败返回 {}，下拉只剩「自定义版本」。
  const [bazelPresets, setBazelPresets] = useState<BazelPresets>({});
  // daily 分支下 Tag 选完后拿到的历史 daily 版本候选(最多 200 条,createTime DESC),
  // 传给下方「基线版本」DailyBaseVersionPicker,由它内部展示历史弹窗;非 daily 分支保持 []。
  const [dailyBaseVersionCandidates, setDailyBaseVersionCandidates] = useState<DailyVersionItem[]>([]);
  // 当前是否选中 Daily Tag(权威信号)。DailyBaseVersionPicker 靠它判定「点历史」时
  // 是走引导还是开弹窗;candidates.length 不够权威(异步/边界会错判)。
  const [dailyTagSelected, setDailyTagSelected] = useState(false);
  // precompiled 分支的 daily subtype 独立持有 candidates/tagSelected(与 buildType='daily'
  // 分支不共享,防止两条路径互相污染)。两个 tab 都用 DailyTagPicker + DailyBaseVersionPicker,
  // 但 onPick 目标 form 字段不同:daily buildType 落 baseVersion(重编);precompiled 的 daily
  // subtype 落 algorithmVersion(直接用现成包)。
  const [precompiledDailyCandidates, setPrecompiledDailyCandidates] = useState<DailyVersionItem[]>([]);
  const [precompiledDailyTagSelected, setPrecompiledDailyTagSelected] = useState(false);
  // 高级选项 · 优先级下拉候选。挂载时从 /job/priority 拉一次;拉失败下拉空,
  // 用户可提交但走后端 default(或 form 里 initialValue=6 兜底)。
  const [priorityList, setPriorityList] = useState<PriorityOption[]>([]);
  useEffect(() => {
    let cancelled = false;
    getPriorityList()
      .then((list) => {
        if (!cancelled) setPriorityList(list);
      })
      .catch((err) => {
        console.error('加载优先级列表失败:', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    let cancelled = false;
    getJobBazelPresets()
      .then((presets) => {
        if (!cancelled) setBazelPresets(presets);
      })
      .catch((e) => {
        // 拉取失败按"不兜底"决策：下拉只剩自定义版本，侦测跳转静默禁用
        console.error('load bazel presets failed:', e);
      });
    return () => { cancelled = true; };
  }, []);

  // ── bazel 编译预设：一键填，复用同一份给「算法包版本侦测自动跳过来」 ──
  // baseVersion 优先级：外部传入 ``carryBaseVersion`` > preset 内置默认。
  // 两条路都会填 baseVersion，不再留空。
  const applyBazelPreset = useCallback((key: string, carryBaseVersion?: string) => {
    const preset = bazelPresets[key];
    if (!preset) {
      message.warning(`预设 ${key} 不存在或已下线，请重新选择`);
      return;
    }
    const carry = (carryBaseVersion || '').trim();
    const effectiveBaseVersion = carry || preset.baseVersion || '';
    // 合并而非覆盖：保留用户已填的仓库分支，预设仅覆盖同名仓库、补充新仓库。
    const currentBranchDpInfo = String(createForm.getFieldValue('branchDpInfo') || '');
    const mergedBranchDpInfo = mergeBranchDpInfo(currentBranchDpInfo, preset.branchDpInfo);
    const hadExisting = deriveChangeModule(currentBranchDpInfo).length > 0;
    const patch: Record<string, unknown> = {
      branchDpInfo: mergedBranchDpInfo,
      changeModule: deriveChangeModule(mergedBranchDpInfo),
    };
    if (effectiveBaseVersion) {
      patch.baseVersion = effectiveBaseVersion;
    }
    createForm.setFieldsValue(patch);
    const mergeHint = hadExisting ? '（已合并你已填的分支，同名仓库以预设为准）' : '';
    message.success(
      carry
        ? `已填入 ${preset.label} 预设分支并带入算法版本到基线${mergeHint}`
        : `已填入 ${preset.label} 预设分支与默认基线版本${mergeHint}`,
    );
  }, [createForm, bazelPresets]);

  // 算法包版本输入侦测：命中任一预设 key 时提示跳 bazel 编译并自动填预设
  const handleAlgoVersionBlur = useCallback((raw: string) => {
    const ver = detectAlgoVersionPreset(raw, Object.keys(bazelPresets));
    if (!ver) return;
    Modal.confirm({
      title: `检测到算法版本 ${ver}`,
      content: `${ver} 算法包存在仿真代码不全的问题，建议改走「bazel 编译」重新编译一份。是否跳转到 bazel 编译并自动填入 ${ver} 预设？基线版本会自动带入当前算法版本。`,
      okText: `跳转 bazel 编译并填 ${ver}`,
      cancelText: '保持算法包版本',
      onOk: () => {
        setBuildType('package_bazel');
        // 同步下拉状态：从 handleAlgoVersionBlur 触发的跳转选中对应版本；
        // 让「一键填版本」下拉与实际填入的字段保持一致，不再显示为「自定义版本」。
        setBazelPresetChoice(ver);
        // 等下一帧渲染出 bazel 块再 setFieldsValue
        setTimeout(() => applyBazelPreset(ver, raw), 0);
      },
    });
  }, [applyBazelPreset, bazelPresets]);
  const [copyDefaults, setCopyDefaults] = useState<CopyDefaults | null>(null);
  // 场景来源页签:'sceneSet'=按场景集;'scene'=按场景名输入;'byJob'=按任务查询
  // (只在 byTaskQuery prop 存在时才在 Segmented 里出现,且内容为只读快照)
  const [sceneTab, setSceneTab] = useState<'sceneSet' | 'scene' | 'byJob' | 'tag'>('sceneSet');
  // 复制「按场景」job 时带入的原始场景 id：未改动场景名输入则按此 id 直接提交，
  // 避免「id→名→id」反查链路出错；用户一旦编辑场景名则置空、回退为按名反查。
  const [copiedSceneIds, setCopiedSceneIds] = useState<number[] | null>(null);

  // ── 定时/延迟/循环创建 Job 排程（docs/schedule-v3.md §5.1） ──
  // 开启后表单顶部 Alert 提示「不走同步 preflight，到点才会暴露分支/包错误」。
  // 三选一：scheduled=定时绝对时间 / delayed=延时相对 now / cron=循环。
  const [enableSchedule, setEnableSchedule] = useState(false);
  const [scheduleMode, setScheduleMode] = useState<'scheduled' | 'delayed' | 'cron'>('scheduled');
  // cron 预演结果（点击「预览」按钮调 /schedules/preview 拉未来 3 次）
  const [cronPreview, setCronPreview] = useState<string[] | null>(null);
  const [cronPreviewLoading, setCronPreviewLoading] = useState(false);

  // 场景管理「发起任务」带入的场景列表（id+name 稳定串作依赖，避免数组身份变化导致 effect 重跑）
  const presetSceneKey = (presetScenes || []).map((s) => `${s.id}:${s.name}`).join('|');
  // effect 内部按需读取最新 presetScenes，但不让它进 deps（数组身份不稳定，已用 presetSceneKey 跟踪）
  const presetScenesRef = useRef(presetScenes);
  presetScenesRef.current = presetScenes;
  const presetSceneSetIdsKey = (presetSceneSetIds || []).map(String).join(',');
  const presetSceneSetIdsRef = useRef(presetSceneSetIds);
  presetSceneSetIdsRef.current = presetSceneSetIds;
  const presetSceneTagIdsKey = (presetSceneTagIds || []).map(String).join(',');
  const presetSceneTagIdsRef = useRef(presetSceneTagIds);
  presetSceneTagIdsRef.current = presetSceneTagIds;
  /**
   * daily 分组虚拟节点编码 token（``L<leafId>G:<groupKey>``）暂存：TreeSelect 不能显示
   * 无对应 value 的节点，所以只走 chip 展示 + 提交时合并进 sceneSetIds 逗号串。
   * open effect 里根据 presetSceneSetIds 中的字符串项初始化。
   */
  const [stagedGroupTokens, setStagedGroupTokens] = useState<string[]>([]);

  const selectedTaskTypeId = Form.useWatch('taskTypeId', createForm);
  // LPL（PNC 仿真）任务才展示「预测数据版本」输入框；其余任务类型不展示也不提交
  // （后端同样按 task_type_code 闸门忽略，双保险）。
  const selectedTaskTypeCode = taskTypes.find(
    (t) => t.id === Number(selectedTaskTypeId),
  )?.taskTypeCode;
  const isLplTaskType = selectedTaskTypeCode === 'LPL';

  // 自定义参数目录的唯一真源：顶层「模型主版本」字段与「高级选项 · 自定义参数」块共用，
  // 避免两处各自拉一次、对同一份目录给出互相矛盾的判断（详见该 hook 注释）。
  const customParameterDefinitions = useCustomParameterDefinitions(open);

  // 「使用编译好的版本 → 版本号 / S3 地址」和「基于已有版本编译 → 基线版本」三处
  // 都是"往 BOS 探一次"语义: 用户填/改 → debounce 500ms → 后端探 → 绿 √/黄 ✕。
  // S3 地址走同一后端 endpoint: PackageUrlResolver 对 ``bos://`` 全路径透传不套模板,
  // 后端从 URL 里同时拆 bucket + key, 支持任意桶(算法包桶 / 用户自贴桶都能探)。
  const algoVersionValue = Form.useWatch('algorithmVersion', createForm) as string | undefined;
  const baseVersionValue = Form.useWatch('baseVersion', createForm) as string | undefined;
  const s3PackageUrlValue = Form.useWatch('s3PackageUrl', createForm) as string | undefined;
  const algoVersionProbe = usePackageExistsProbe(
    algoVersionValue,
    'package_neodata',
    buildType === 'precompiled' && precompiledSubtype === 'version',
  );
  const baseVersionProbe = usePackageExistsProbe(
    baseVersionValue,
    'package_bazel',
    buildType === 'package_bazel' || buildType === 'daily',
  );
  // S3 tab: 只在 URL 看起来是 bos:// 开头时才发探测请求, 避免用户键入前几个字符时
  // 后端把它当空版本号+走模板拼一个假 URL 返回"不存在"误报; 前缀合法性由表单 rules 校验。
  const s3PackageUrlProbe = usePackageExistsProbe(
    s3PackageUrlValue,
    'package_neodata',
    buildType === 'precompiled'
      && precompiledSubtype === 's3'
      && /^bos:\/\//i.test((s3PackageUrlValue ?? '').trim()),
  );

  // 「预测数据版本」默认模式来自系统参数（admin 可调；建任务弹窗走普通用户可读别名接口）。
  // 实际填入 default / 该 tag 当前生效版本号由 Picker 在版本列表就绪后完成；
  // 复制回填 / 用户已选值优先，不覆盖。
  const [predictionDefaultVersion, setPredictionDefaultVersion] = useState('latest');
  // 弹窗打开时的 resetFields 会清掉子组件首帧自动填入的默认值；递增此计数
  // 让 Picker 在 reset / 复制回填完成后再跑一次默认值补齐。
  const [formEpoch, setFormEpoch] = useState(0);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    getJobPredictionDefaultVersion()
      .then((value) => { if (!cancelled) setPredictionDefaultVersion(value); })
      .catch(() => { /* 读不到沿用 latest（后端无 current 时同样回落 default） */ });
    return () => { cancelled = true; };
  }, [open]);

  // 预测数据版本号（dataVersion）：与 CreatePredictionUpdateJobModal 同口径，从当前创建类型的
  // 版本来源解析（precompiled: S3 地址 / 版本号；bazel / daily: 基线版本）。
  const predictionTagSourceValue = buildType === 'precompiled'
    ? (precompiledSubtype === 's3' ? s3PackageUrlValue : algoVersionValue)
    : baseVersionValue;
  const predictionDataVersion = extractPredictionDataVersion(predictionTagSourceValue);

  // 加载任务类型；默认选中第一个启用项
  const loadTaskTypes = useCallback(async () => {
    // 感知数据刷写页：任务类型锁定为 PE_DATA（enable_flag=0，通用列表里本来就选不了）。
    if (perceptionRefreshTaskType) {
      setTaskTypes([{ ...perceptionRefreshTaskType, taskTypeCode: 'PE_DATA', enableFlag: 1 }]);
      createForm.setFieldsValue({ taskTypeId: perceptionRefreshTaskType.id });
      return;
    }
    try {
      const list = await getTaskTypes();
      setTaskTypes(list);
      const enabledTypes = list.filter(
        (type) => type.enableFlag === undefined || type.enableFlag === 1,
      );
      if (enabledTypes.length > 0) {
        createForm.setFieldsValue({ taskTypeId: enabledTypes[0].id });
      }
    } catch (err) {
      console.error('加载任务类型列表失败:', err);
    }
  }, [createForm, perceptionRefreshTaskType]);

  // 「按场景集合」页签需要场景树 + daily 子树的分组统计（TreeSelect 里展开分组虚拟子节点）
  const loadSceneTree = useCallback(async () => {
    try {
      const tree = await getSceneTree(true);
      setSceneTree(tree);
      // daily 子树叶子（node_flag=1）拉一次分组统计，用来挂虚拟子节点
      const dailyRoot = tree.find(n => n.name === 'daily' && n.parentId === -1);
      if (dailyRoot) {
        const dailyLeafIds: number[] = [];
        const walk = (n: SceneTreeNode) => {
          if (n.nodeFlag === 1) dailyLeafIds.push(n.id);
          n.children?.forEach(walk);
        };
        walk(dailyRoot);
        if (dailyLeafIds.length) {
          const entries = await Promise.all(dailyLeafIds.map(async (id) => {
            try {
              const stats = await getSceneGroupStats([id]);
              return [id, stats] as const;
            } catch {
              return [id, []] as const;
            }
          }));
          setDailyGroupStats(Object.fromEntries(entries));
        }
      }
    } catch (err) {
      console.error('加载场景树失败:', err);
    }
  }, []);

  // 「按场景标签」页签（无 presetSceneTagIds 时）按需加载标签树；每次打开弹窗重置，
  // 保证用户在其他页面增删标签后能拿到最新树。失败允许再次触发重试。
  const loadTagTree = useCallback(async () => {
    if (tagTreeRequestedRef.current) return;
    tagTreeRequestedRef.current = true;
    setTagTreeLoading(true);
    try {
      const tree = await getSceneTagTree();
      setTagTree(tree);
    } catch (err) {
      console.error('加载标签树失败:', err);
      message.error('加载场景标签失败，请稍后重试');
      tagTreeRequestedRef.current = false;
    } finally {
      setTagTreeLoading(false);
    }
  }, []);

  // 按场景 id 预填「按场景」页签（复制 job 用）：调用方只有 id，本地无 name，需反查 scene_inspection。
  // 直查不受场景树归属限制；按场景发起的场景常不在任何场景集树下。
  const prefillSceneTabByIds = useCallback(async (ids: number[]) => {
    setSceneTab('scene');
    setCopiedSceneIds(ids.length > 0 ? ids : null);
    let sceneNames: string[] = [];
    if (ids.length > 0) {
      try {
        const res = await getSceneInspectionList({ ids, current: 1, pageSize: ids.length });
        sceneNames = (res.list || []).map((s) => s.name).filter(Boolean);
      } catch (err) {
        console.error('场景 id 反查场景名失败:', err);
        message.warning('场景名解析失败，请手动确认场景名');
      }
    }
    createForm.setFieldsValue({ sceneNames });
  }, [createForm]);

  // 场景管理「发起任务」预填：调用方已带 id+name，直接填，不调接口。
  const prefillSceneTabByScenes = useCallback((scenes: PresetScene[]) => {
    setSceneTab('scene');
    setCopiedSceneIds(scenes.length > 0 ? scenes.map((s) => s.id) : null);
    createForm.setFieldsValue({
      sceneNames: scenes.map((s) => s.name).filter(Boolean),
    });
  }, [createForm]);

  // 把原 job 的发起参数回填进表单（复制 job）。
  // 场景来源按原 job 的 sceneSource 区分回填到对应页签：
  // - scene_set：场景集 id 回填 TreeSelect；
  // - scene：sceneSetIds 实为场景 id 串，反查场景名回填「按场景」页签（不复用 TreeSelect）。
  const applyCopyRecord = useCallback(async (record: JobItem) => {
    let packageVersion = '';
    let commitInput = '';
    const bz = record.bazelInputs || {};
    const baseVersion = (bz.base_version || '').trim();
    const versionDescription = bz.version_description ?? 'test';
    const changeModule = bz.change_module || '';
    const branchDpInfo = (bz.branch_dp_info || '')
      .split(',')
      .map((entry: string) => entry.trim())
      .filter(Boolean)
      .map((entry: string) => entry.replace(/:/, ' '))
      .join('\n');

    // record.algorithmVersion 是后端 snapshot 后的「完整包路径」：
    // - package_neodata 短版本号 → 走模板拼好的完整 URL
    // - 用户原填 bos:///obs:// 全路径 → 透传保持原样
    // - bazel 编译 → bazel 自动生成的 version snapshot
    // 因此判定「是否 bos:// 全路径」必须看 snapshot 字段，而不是 buildSpec 里用户原输入。
    const fullPackageUrl = (record.algorithmVersion || '').trim();
    if (record.buildType === 'compile_platform') {
      packageVersion = fullPackageUrl;
      if (record.buildSpec?.type === 'compile_platform') {
        commitInput = commitIdToTextarea(record.buildSpec.commitId);
      }
    } else {
      packageVersion =
        (record.buildSpec?.type === 'package_neodata' || record.buildSpec?.type === 'package_bazel') &&
        record.buildSpec.algorithmVersion
          ? record.buildSpec.algorithmVersion
          : fullPackageUrl;
    }

    setCopyDefaults({
      algorithmVersion: packageVersion,
      fullPackageUrl,
      branchCommitInput: commitInput,
      baseVersion,
      versionDescription,
      changeModule,
      branchDpInfo,
    });
    // UI 类型判定：
    // - package_bazel / compile_platform 走各自分支
    // - 其余合并到 precompiled；再按 snapshot 全路径是否 bos:// 决定子选项卡
    let initialUiType: UiBuildType;
    let initialPrecompiledSub: PrecompiledSubtype = 'version';
    if (record.buildType === 'package_bazel') {
      initialUiType = 'package_bazel';
    } else if (record.buildType === 'compile_platform') {
      // cmake 编译已废弃(2026-08-26):UI 里 daily radio 取代了原 compile_platform,
      // 复制老 job 时回退到 precompiled 兼容(用户能看到原 algorithmVersion 提示,
      // 但无法真正"复制"cmake 编译类型)。
      initialUiType = 'precompiled';
    } else {
      initialUiType = 'precompiled';
      initialPrecompiledSub = /^bos:\/\//i.test(fullPackageUrl) ? 's3' : 'version';
    }
    setBuildType(initialUiType);
    setPrecompiledSubtype(initialPrecompiledSub);
    // 复制场景下的 bazel 一键填选择：Job 是已知版本时用户能自主选，
    // 这里统一回到「自定义版本」，避免覆盖用户复制过来的原始 baseVersion/branchDpInfo。
    setBazelPresetChoice('custom');

    const isS3 = initialUiType === 'precompiled' && initialPrecompiledSub === 's3';
    // 模型主版本已提到顶层字段，回填时从 customParams 里拆出来单独写，其余留在自定义参数列表。
    const { modelRelease, entries: customParameterEntries } = splitModelReleaseParams(record.customParams);
    const baseFields = {
      taskTypeId: record.taskTypeId,
      customParameterEntries,
      modelRelease,
      priority: record.priority,
      // LPL 预测数据版本：复制时回填源 Job 的实际生效值（default / 具体版本号）。
      // 非 LPL / 存量 job 无此字段 → undefined，留空走系统默认。
      predictionDataVersion: record.predictionDataVersion || undefined,
      jobName: record.jobName ? buildCopyName(record.jobName) : undefined,
      // 「S3 地址」子选项卡填 snapshot 全路径；「版本号」子选项卡填原短版本号。
      algorithmVersion: isS3 ? undefined : packageVersion,
      s3PackageUrl: isS3 ? fullPackageUrl : undefined,
      branchCommitInput: commitInput,
      baseVersion,
      versionDescription,
      changeModule,
      branchDpInfo,
    };

    const rawSceneTokens = (record.sceneSetIds || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    // 「按任务查询」页签只从 JobTaskList「基于当前查询发起任务」按钮进入,
    // 复制入口不再自动预填该 tab 的字段。想按状态反查 → 去 TaskList 页筛完再发。

    if (record.sceneSource === 'scene') {
      // 按场景发起的 job：sceneSetIds 为场景 id 串，回填「按场景」页签（带原始 id + 反查名）
      createForm.setFieldsValue(baseFields);
      const ids = rawSceneTokens.map(Number).filter((n) => !Number.isNaN(n));
      await prefillSceneTabByIds(ids);
    } else {
      setSceneTab('sceneSet');
      const sceneSetIds = rawSceneTokens
        .map(Number)
        .filter((n) => !Number.isNaN(n));
      createForm.setFieldsValue({ ...baseFields, sceneSetIds });
    }
  }, [createForm, prefillSceneTabByIds]);

  // 弹窗打开：重置 → 加载任务类型（非预选模式加载场景树）→ 复制回填
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCopyDefaults(null);
    // 复制含参 Job 时默认展开高级选项；模型主版本已移到上层，只有"其余自定义参数"非空才需要展开。
    setAdvancedOpen(splitModelReleaseParams(copyRecord?.customParams).entries.length > 0);
    setCopiedSceneIds(null);
    setBuildType(copyRecord?.buildType === 'package_bazel' ? 'package_bazel' : DEFAULT_BUILD_TYPE);
    setPrecompiledSubtype('version');
    setBazelPresetChoice('custom');
    setSceneTab('sceneSet');
    // 定时开关每次开弹窗默认关（不沿用上次会话残留）
    setEnableSchedule(false);
    setScheduleMode('scheduled');
    setCronPreview(null);
    createForm.resetFields();
    // 「按场景标签」标签树每次开窗按需重拉，拿到其他页面刚增删的标签。
    tagTreeRequestedRef.current = false;
    (async () => {
      // 必须先 await loadTaskTypes（它会默认选中第一个启用项），再做回填，
      // 否则带入的 taskTypeId 会被随后到达的默认值覆盖。两种页签都可能用到场景树。
      await Promise.all([loadTaskTypes(), loadSceneTree()]);
      if (cancelled) return;
      if (copyRecord) {
        await applyCopyRecord(copyRecord);
        // 感知数据刷写：回填原 Job 手填的版本 tag，复制出的新版本仍归在原分组。
        if (perceptionRefreshTaskType && copyRecord.perceptionTag) {
          createForm.setFieldsValue({ perceptionTag: copyRecord.perceptionTag });
        }
        // 「按任务查询 + 复制源 Job 元信息」组合入口(对比页「基于当前查询发起任务」):
        // applyCopyRecord 已经把源 Job 的算法版本/baseVersion/branchDpInfo/优先级/
        // 任务类型/jobName(带 _copy 后缀) 填进 form,但同时把场景池字段(sceneIds /
        // sceneSetIds) 也填了 —— 这些在 byJob 页签下不会被读取(提交路径见下方
        // sceneSourcePayload 组装 sceneTab==='byJob' 分支只读 byTaskQuery),
        // 所以直接把 sceneTab 切回 byJob 即可,场景池仍锁在调用方冻结的 filters。
        if (byTaskQuery) setSceneTab('byJob');
      } else if (presetScenesRef.current && presetScenesRef.current.length > 0) {
        // 场景管理「发起任务」：调用方已持有 id+name，直接预填，不再走反查接口
        prefillSceneTabByScenes(presetScenesRef.current);
      } else if (presetSceneSetIdsRef.current && presetSceneSetIdsRef.current.length > 0) {
        // 场景管理「勾选整个集合发起任务」：预填「按场景集合」TreeSelect 的 sceneSetIds。
        // 拆分 number（塞 TreeSelect）+ string（分组虚拟 token，走 stagedGroupTokens）；
        // 提交时合并成完整 sceneSetIds 串（后端 job_service._parse_scene_set_tokens 解析）。
        const nums: number[] = [];
        const strs: string[] = [];
        for (const v of presetSceneSetIdsRef.current) {
          if (typeof v === 'number') nums.push(v);
          else if (typeof v === 'string' && v) strs.push(v);
        }
        setSceneTab('sceneSet');
        setStagedGroupTokens(strs);
        createForm.setFieldsValue({ sceneSetIds: nums });
      } else if (presetSceneTagIdsRef.current && presetSceneTagIdsRef.current.length > 0) {
        // 场景管理「标签检索」选中标签点发起任务：切到「按场景标签」页签，
        // 标签 id 已由调用方锁定，只读展示；提交时把标签 id 数组拼 sceneTagIds 逗号串。
        setSceneTab('tag');
      } else if (byTaskQuery) {
        // 「按任务查询」入口:切到 byJob 页签,场景池已由调用方锁定,弹窗内只需
        // 配置版本/参数;filter 展示走 byTaskQuery.filterEntries 只读渲染。
        setSceneTab('byJob');
      } else {
        setStagedGroupTokens([]);
      }
      // resetFields / 复制回填已完成，通知预测数据版本 Picker 补默认值。
      setFormEpoch(current => current + 1);
    })();
    return () => { cancelled = true; };
  }, [
    open, copyRecord, presetSceneKey, presetSceneSetIdsKey, presetSceneTagIdsKey,
    byTaskQuery,
    loadTaskTypes, loadSceneTree, applyCopyRecord, prefillSceneTabByScenes, createForm, perceptionRefreshTaskType,
  ]);

  // 「按场景标签」页签：无预设标签时，切到该页签后再懒加载标签树（不拖慢其他来源的开窗）。
  useEffect(() => {
    if (!open || sceneTab !== 'tag') return;
    if (presetSceneTagIds && presetSceneTagIds.length > 0) return;
    void loadTagTree();
  }, [open, sceneTab, presetSceneTagIds, loadTagTree]);

  // 选择任务类型后自动生成默认任务名（复制场景保留复制来的名称，不覆盖）
  useEffect(() => {
    if (selectedTaskTypeId && open && taskTypes.length > 0 && !copyDefaults) {
      const taskType = taskTypes.find((t) => t.id === selectedTaskTypeId);
      if (taskType?.name) {
        const now = new Date();
        const timestamp = [
          now.getFullYear(),
          String(now.getMonth() + 1).padStart(2, '0'),
          String(now.getDate()).padStart(2, '0'),
          '_',
          String(now.getHours()).padStart(2, '0'),
          String(now.getMinutes()).padStart(2, '0'),
          String(now.getSeconds()).padStart(2, '0'),
          '_',
          String(now.getMilliseconds()).padStart(3, '0'),
        ].join('');
        createForm.setFieldsValue({ jobName: `${taskType.name}_${timestamp}` });
      }
    }
  }, [selectedTaskTypeId, open, createForm, taskTypes, copyDefaults]);

  // 将场景树转换为 TreeSelect 格式：
  // - 剔除 is_hidden=true 节点及后代
  // - daily 子树的叶子节点下挂 scene_group 分组虚拟子节点，value=后端 token
  //   `L<leafId>G:<groupKey>`（提交时进 sceneSetIds 逗号串，后端
  //   job_service._parse_scene_set_tokens 解析）
  const convertSceneTreeToTreeData = (nodes: SceneTreeNode[]): any[] => {
    return nodes
      .filter((node) => !node.isHidden)
      .map((node) => {
        const realChildren = node.children && node.children.length > 0
          ? convertSceneTreeToTreeData(node.children)
          : undefined;

        // daily 子树叶子：把该 leaf 的 scene_group 分组挂成 TreeSelect 可选子节点
        let virtualGroupChildren: any[] | undefined;
        if (node.nodeFlag === 1 && dailyGroupStats[node.id]?.length) {
          virtualGroupChildren = dailyGroupStats[node.id].map((g) => ({
            title: sceneGroupDisplay(g.sceneGroup),
            value: encodeGroupBackendToken(node.id, g.sceneGroup),
            key: encodeGroupBackendToken(node.id, g.sceneGroup),
            isLeaf: true,
          }));
        }

        // 合并真实子节点与虚拟子节点（虚拟节点排后面便于视觉分层）
        const children = realChildren || virtualGroupChildren
          ? [...(realChildren || []), ...(virtualGroupChildren || [])]
          : undefined;

        return {
          title: node.name,
          value: node.id,
          key: node.id,
          children,
        };
      });
  };

  // 标签树 → TreeSelect。目录仅作分组导航(置灰不可选)，只有叶子标签可勾选；
// 多标签提交后后端按「组内并集、组间交集」实时展开场景。
  const convertTagTreeToTreeData = (nodes: SceneTagNode[]): any[] =>
    nodes.map((node) => ({
      title: tagDisplayName(node),
      value: node.id,
      key: node.id,
      disabled: node.nodeFlag !== 1,
      children: node.children && node.children.length > 0
        ? convertTagTreeToTreeData(node.children)
        : undefined,
    }));

  const handleCancel = () => {
    setCopyDefaults(null);
    setEnableSchedule(false);
    setScheduleMode('scheduled');
    setCronPreview(null);
    createForm.resetFields();
    onClose();
  };

  /**
   * 把用户输入的场景名精确反查为场景 id（「按场景·输入场景名」提交前调用）。
   * 走 ``POST /scene/inspection/resolve-ids-by-names`` —— **仅接受质检合格
   * (COMPLETED) 场景**；命中未合格或未找到时后端抛错、axios 拦截器自动
   * message.error 提示用户，本函数返 null 终止任务创建流程。
   */
  const resolveSceneIdsByNames = (names: string[]): Promise<number[] | null> =>
    resolveSceneIdsByNamesApi(names);

  const handleSubmit = async () => {
    try {
      const values = await createForm.validateFields();
      // 顶层「模型主版本」字段是唯一真源：并回 customParams，同时丢弃列表里可能残留的同名项。
      const customParams = mergeModelReleaseParams(values.customParameterEntries, values.modelRelease);

      // LPL「预测数据版本」：仅 LPL 任务类型提交；非 LPL 即便表单有残留值也不带
      // （后端同样按 task_type_code 闸门忽略，双保险）。留空 = 后端走系统参数默认值。
      const submitTaskType = taskTypes.find((t) => t.id === Number(values.taskTypeId));
      const predictionDataVersion = submitTaskType?.taskTypeCode === 'LPL'
        ? (values.predictionDataVersion || '').trim() || undefined
        : undefined;
      const predictionDataVersionPayload = {
        ...(predictionDataVersion ? { predictionDataVersion } : {}),
        // 感知数据刷写：可选版本 tag，只在锁定 PE_DATA 时提交。
        ...(perceptionRefreshTaskType && (values.perceptionTag || '').trim()
          ? { perceptionTag: (values.perceptionTag as string).trim() }
          : {}),
      };

      setCreateLoading(true);

      // 计算场景来源载荷（统一归到按场景集 sceneSetIds / 按场景 sceneIds / 按任务查询反查）:
      // - sceneTab='byJob':前端不搬 sceneIds,把 (sourceJobId, filters) 作为
      //   byTaskQuery 交后端反查填充;提交路径与「按场景」相同(scene_source=scene)。
      //   filters 是 TaskList 页 queryParams 的完整快照,后端复用列表 SQL builder。
      // - sceneTab='scene':带入的原始 id(复制/预选未改动)直接用,否则按输入场景名反查 id;
      // - sceneTab='sceneSet':TreeSelect 多选的 sceneSetIds 逗号串。
      let sceneSourcePayload: {
        sceneIds?: number[];
        sceneSetIds?: string;
        sceneTagIds?: string;
        byTaskQuery?: { jobId: number; filters: Record<string, unknown> };
      };
      if (sceneTab === 'byJob') {
        // byJob 页签场景池锁死为 byTaskQuery prop 里 jobId+filters 组合;
        // 无 byTaskQuery 却停留在 byJob(不该发生,防御性检查一下)。
        if (!byTaskQuery) {
          message.error('缺少「按任务查询」参数,请回到 task 列表页重新发起');
          return;
        }
        sceneSourcePayload = {
          byTaskQuery: {
            jobId: byTaskQuery.jobId,
            filters: byTaskQuery.filters,
          },
        };
      } else if (sceneTab === 'tag') {
        // 「按场景标签」两种入口：
        // - 场景管理带入：标签 id 由调用方锁定（presetSceneTagIds，只读展示）；
        // - 任务列表直接创建：用户在弹窗内自选标签（form.sceneTagIds）。
        const tagIds = (presetSceneTagIds && presetSceneTagIds.length > 0)
          ? presetSceneTagIds
          : ((values.sceneTagIds as number[] | undefined) || []);
        if (tagIds.length === 0) {
          message.error('请至少选择一个场景标签');
          return;
        }
        sceneSourcePayload = { sceneTagIds: tagIds.map(String).join(',') };
      } else if (sceneTab === 'scene') {
        // 复制/预选带入且未改动场景名：直接用原始场景 id（不依赖场景名反查链路）
        if (copiedSceneIds && copiedSceneIds.length > 0) {
          if (copiedSceneIds.length > MAX_SCENE_IDS_PER_JOB) {
            message.error(`按场景发起最多 ${MAX_SCENE_IDS_PER_JOB} 个场景，当前 ${copiedSceneIds.length} 个`);
            return;
          }
          sceneSourcePayload = { sceneIds: copiedSceneIds };
        } else {
          const names: string[] = Array.from(
            new Set(
              ((values.sceneNames as string[]) || [])
                .map((n) => (n || '').trim())
                .filter(Boolean),
            ),
          );
          if (names.length === 0) {
            message.error('请输入场景名称');
            return;
          }
          if (names.length > MAX_SCENE_IDS_PER_JOB) {
            message.error(`按场景发起最多 ${MAX_SCENE_IDS_PER_JOB} 个场景，当前 ${names.length} 个`);
            return;
          }
          const resolvedIds = await resolveSceneIdsByNames(names);
          if (!resolvedIds) {
            return; // 反查失败 / 有不存在的场景名，错误已提示
          }
          sceneSourcePayload = { sceneIds: resolvedIds };
        }
      } else {
        const sceneSetIdsArray = Array.isArray(values.sceneSetIds)
          ? values.sceneSetIds.map((id: number | string) => String(id))
          : [];
        // 场景管理「勾选 daily 分组虚拟节点」带入的 L<leafId>G:<groupKey> token
        // 走 stagedGroupTokens（TreeSelect 不能显示无 value 的节点），提交时合并进串。
        // 后端 job_service._parse_scene_set_tokens 会拆分「纯数字」与「L…G:…」token。
        const merged = [...sceneSetIdsArray, ...stagedGroupTokens];
        if (merged.length === 0) {
          message.error('请至少选择一个场景集合');
          setCreateLoading(false);
          return;
        }
        sceneSourcePayload = { sceneSetIds: merged.join(',') };
      }

      // 高级选项：重复运行次数 1-10，默认 1。仅 N>1 时透传给后端走循环创建。
      const repeatTimes = Math.max(1, Math.min(10, Number(values.repeatTimes) || 1));
      const repeatSuffix = repeatTimes > 1 ? `（共 ${repeatTimes} 个）` : '';

      // ── 定时/延迟/循环创建 Job 排程（docs/schedule-v3.md §5.2） ──
      // 开启定时后不走原有立即建 Job 流程，而是把 create 入参完整快照丢给后端，
      // 由 schedule_loop 协程到点同进程 await 对应异步 service 方法。
      // 「开启定时」不走同步 preflight——分支/包对错到点才会暴露，前端必须自行
      // 单次触发确保参数正确。
      if (enableSchedule) {
        // 校验定时相关字段
        if (scheduleMode === 'scheduled') {
          const runAt = values.runAt as Dayjs | undefined;
          if (!runAt) {
            message.error('请选择定时触发时间');
            setCreateLoading(false);
            return;
          }
          // 不得早于 now + 1min
          if (runAt.toDate().getTime() < Date.now() + 60_000) {
            message.error('定时时间不得早于当前时间 + 1 分钟');
            setCreateLoading(false);
            return;
          }
        } else if (scheduleMode === 'delayed') {
          const dv = Number(values.delayValue);
          if (!Number.isFinite(dv) || dv < 1) {
            message.error('请输入有效的延时数值（≥1）');
            setCreateLoading(false);
            return;
          }
        } else {
          // cron
          const expr = (values.cronExpr || '').trim();
          if (!expr) {
            message.error('请输入 cron 表达式');
            setCreateLoading(false);
            return;
          }
        }

        // 推断 job_endpoint 与 payload 快照（与下方立即建 Job 流程对齐）
        let jobEndpoint: 'create' | 'bazel_create';
        let payload: Record<string, any>;
        if (buildType === 'package_bazel' || buildType === 'daily') {
          const baseVersion = (values.baseVersion || '').trim();
          if (!baseVersion) {
            message.error('请输入基线版本');
            setCreateLoading(false);
            return;
          }
          jobEndpoint = 'bazel_create';
          payload = {
            taskTypeId: Number(values.taskTypeId),
            jobName: values.jobName || undefined,
            priority: values.priority !== undefined ? Number(values.priority) : undefined,
            ...sceneSourcePayload,
            ...(customParams ? { customParams } : {}),
            ...predictionDataVersionPayload,
            baseVersion,
            versionDescription:
              (values.versionDescription || '').trim() ||
              (() => {
                const tt = taskTypes.find((t) => t.id === Number(values.taskTypeId));
                return tt?.name ? `${tt.name}仿真` : '仿真';
              })(),
            changeModule: (values.changeModule || '').trim(),
            branchDpInfo: normalizeBranchDpInfoForApi(values.branchDpInfo || ''),
            ...(repeatTimes > 1 ? { repeatTimes } : {}),
            // 「一键填入」选到具体预设时把 key 作为 version 号前缀；custom 不传。
            // daily 分支不启用「一键填版本」,永远不带 presetKey。
            ...(buildType === 'package_bazel' && bazelPresetChoice !== 'custom'
              ? { presetKey: bazelPresetChoice } : {}),
          } satisfies CreateBazelJobParams;
        } else {
          // 落到 create 端点的只剩 precompiled;package_bazel / daily 都走上面 bazel_create 分支
          jobEndpoint = 'create';
          let resolvedBuildType: BuildType;
          let buildSpec: BuildSpec;
          if (buildType === 'precompiled') {
            if (precompiledSubtype === 's3') {
              const bosUrl = (values.s3PackageUrl || '').trim();
              if (!/^(bos|obs):/i.test(bosUrl)) {
                message.error('S3 地址必须以 bos:// 或 obs:// 开头');
                setCreateLoading(false);
                return;
              }
              resolvedBuildType = 'package_neodata';
              buildSpec = { type: 'package_neodata', algorithmVersion: bosUrl };
            } else {
              const ver = (values.algorithmVersion || '').trim();
              if (!ver) {
                message.error('请输入版本号');
                setCreateLoading(false);
                return;
              }
              resolvedBuildType = 'package_neodata';
              buildSpec = { type: 'package_neodata', algorithmVersion: ver };
            }
          } else {
            // 兜底(理论不可达):UI 里已删除 compile_platform,新的 daily 走 bazel_create
            message.error(`未知的 buildType=${String(buildType)},请刷新页面重试`);
            setCreateLoading(false);
            return;
          }
          payload = {
            priority: Number(values.priority),
            taskTypeId: Number(values.taskTypeId),
            jobName: values.jobName || undefined,
            ...sceneSourcePayload,
            ...(customParams ? { customParams } : {}),
            ...predictionDataVersionPayload,
            buildType: resolvedBuildType,
            buildSpec,
            ...(repeatTimes > 1 ? { repeatTimes } : {}),
          } satisfies CreateJobParams;
        }

        // 装配排程请求体（kind 三选一字段互斥）
        const scheduleBody: import('@apis/simulation').ScheduleCreatePayload = {
          name: (values.jobName || '').toString(),
          job_endpoint: jobEndpoint,
          payload,
          kind:
            scheduleMode === 'scheduled' ? 'once_at'
            : scheduleMode === 'delayed' ? 'once_after'
            : 'cron',
        };
        if (scheduleMode === 'scheduled') {
          scheduleBody.fire_at = (values.runAt as Dayjs).toISOString();
        } else if (scheduleMode === 'delayed') {
          scheduleBody.delay_value = Number(values.delayValue);
          scheduleBody.delay_unit = (values.delayUnit as ScheduleDelayUnit) || 'minutes';
        } else {
          scheduleBody.cron_expr = (values.cronExpr || '').trim();
          scheduleBody.cron_tz = 'Asia/Shanghai';
        }
        await createSchedule(scheduleBody);
        message.success('定时已创建，到点将自动触发');
        setCopyDefaults(null);
        setEnableSchedule(false);
        setScheduleMode('scheduled');
        setCronPreview(null);
        createForm.resetFields();
        onClose();
        onSuccess?.();
        return;
      }

      if (buildType === 'package_bazel' || buildType === 'daily') {
        const baseVersion = (values.baseVersion || '').trim();
        if (!baseVersion) {
          message.error('请输入基线版本');
          setCreateLoading(false);
          return;
        }
        const bazelParams: CreateBazelJobParams = {
          taskTypeId: Number(values.taskTypeId),
          jobName: values.jobName || undefined,
          priority: values.priority !== undefined ? Number(values.priority) : undefined,
          ...sceneSourcePayload,
          ...(customParams ? { customParams } : {}),
          ...predictionDataVersionPayload,
          baseVersion,
          versionDescription:
            (values.versionDescription || '').trim() ||
            (() => {
              const tt = taskTypes.find((t) => t.id === Number(values.taskTypeId));
              return tt?.name ? `${tt.name}仿真` : '仿真';
            })(),
          changeModule: (values.changeModule || '').trim(),
          branchDpInfo: normalizeBranchDpInfoForApi(values.branchDpInfo || ''),
          ...(repeatTimes > 1 ? { repeatTimes } : {}),
          // 「一键填入」选到具体预设时把 key 作为 version 号前缀；custom 不传。
          // daily 分支不启用「一键填版本」,永远不带 presetKey。
          ...(buildType === 'package_bazel' && bazelPresetChoice !== 'custom'
            ? { presetKey: bazelPresetChoice } : {}),
        };
        if (dryRunMode) {
          // dry-run:baseVersion 替换为占位符,把 params 回传给上层生成 jobParams JSON。
          const dryParams = {
            ...(bazelParams as unknown as Record<string, unknown>),
            baseVersion: versionPlaceholder,
          };
          onDryRunResult?.({ endpoint: 'bazel_create', params: dryParams });
          setCopyDefaults(null);
          createForm.resetFields();
          onClose();
          return;
        }
        const result = await createBazelJob(bazelParams);
        message.success(
          `已发起 bazel 编译${repeatSuffix}（版本 ${result.version}），编译完成后自动调度`,
        );
      } else {
        // precompiled 是前端 UI 合并（版本号 + S3 地址），都映射到后端 package_neodata。
        // 后端 PackageUrlResolver 对 bos:// / obs:// 完整路径透传不套模板，对短版本号套模板。
        let resolvedBuildType: BuildType;
        let buildSpec: BuildSpec;
        if (buildType === 'precompiled') {
          if (precompiledSubtype === 's3') {
            const bosUrl = (values.s3PackageUrl || '').trim();
            if (!/^bos:\/\//i.test(bosUrl)) {
              message.error('S3 地址必须以 bos:// 开头');
              setCreateLoading(false);
              return;
            }
            resolvedBuildType = 'package_neodata';
            buildSpec = { type: 'package_neodata', algorithmVersion: bosUrl };
          } else {
            const ver = (values.algorithmVersion || '').trim();
            if (!ver) {
              message.error('请输入版本号');
              setCreateLoading(false);
              return;
            }
            resolvedBuildType = 'package_neodata';
            buildSpec = { type: 'package_neodata', algorithmVersion: ver };
          }
        } else {
          // 剩 precompiled;package_bazel / daily 都已在上面 bazel_create 分支处理
          message.error(`未知的 buildType=${String(buildType)},请刷新页面重试`);
          setCreateLoading(false);
          return;
        }
        const params: CreateJobParams = {
          priority: Number(values.priority),
          taskTypeId: Number(values.taskTypeId),
          jobName: values.jobName || undefined,
          ...sceneSourcePayload,
          ...(customParams ? { customParams } : {}),
          ...predictionDataVersionPayload,
          buildType: resolvedBuildType,
          buildSpec,
          ...(repeatTimes > 1 ? { repeatTimes } : {}),
        };
        if (dryRunMode) {
          // dry-run:buildSpec.algorithmVersion 替换为占位符,把 params 回传给上层。
          const dryBuildSpec = {
            ...(buildSpec as unknown as Record<string, unknown>),
            algorithmVersion: versionPlaceholder,
          };
          const dryParams = {
            ...(params as unknown as Record<string, unknown>),
            buildSpec: dryBuildSpec,
          };
          onDryRunResult?.({ endpoint: 'create', params: dryParams });
          setCopyDefaults(null);
          createForm.resetFields();
          onClose();
          return;
        }
        await createJob(params);
        message.success(`Job 创建成功${repeatSuffix}`);
      }
      setCopyDefaults(null);
      createForm.resetFields();
      onClose();
      onSuccess?.();
    } catch (err: any) {
      if (err?.errorFields) {
        if (err.errorFields.some((field: { name: (string | number)[] }) => field.name[0] === 'customParameterEntries')) {
          setAdvancedOpen(true);
        }
        return; // 表单校验错误
      }
      console.error('创建任务失败:', err);
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <Modal
      title="创建 Job"
      open={open}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>取消</Button>,
        <Button key="submit" type="primary" loading={createLoading} onClick={handleSubmit}>
          创建 Job
        </Button>,
      ]}
      width={600}
      destroyOnHidden
    >
      <Form
        form={createForm}
        layout="vertical"
        style={{ marginTop: 20 }}
        onValuesChange={(changed) => {
          // 用户改动了场景名输入 → 放弃复制带入的原始场景 id，改走按名反查
          if ('sceneNames' in changed && copiedSceneIds) {
            setCopiedSceneIds(null);
          }
        }}
      >
        {enableSchedule && (
          <Alert
            type="warning"
            showIcon
            message="开启定时功能后将不在创建过程检查包以及分支是否正确，只会在运行时直接报错，请自行单次触发确保参数正确"
            style={{ marginBottom: 16 }}
          />
        )}

        <Form.Item
          name="taskTypeId"
          label="任务类型"
          rules={[{ required: true, message: '请选择任务类型' }]}
        >
          <Select
            placeholder="请选择任务类型"
            style={{ width: '100%' }}
            disabled={!!perceptionRefreshTaskType}
          >
            {taskTypes.map((type) => (
              <Select.Option key={type.id} value={type.id} disabled={type.enableFlag === 0}>
                {type.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="创建类型"
          required
          tooltip={
            <div style={{ whiteSpace: 'pre-line' }}>
              {`使用编译好的版本：${BUILD_TYPE_TOOLTIPS.precompiled}\n基于已有版本编译：${BUILD_TYPE_TOOLTIPS.package_bazel}\n基于 daily 版本：${BUILD_TYPE_TOOLTIPS.daily}`}
            </div>
          }
        >
          <Radio.Group
            value={buildType}
            onChange={(e) => {
              const next = e.target.value as UiBuildType;
              setBuildType(next);
              // 切走 daily 时把 daily 相关的父层状态重置,避免下次切回 daily 残留
              // 上一次的 tag 已选态和历史列表,让引导判断 stale。
              if (next !== 'daily') {
                setDailyBaseVersionCandidates([]);
                setDailyTagSelected(false);
              }
              if (copyDefaults) {
                if (next === 'package_bazel' || next === 'daily') {
                  createForm.setFieldsValue({
                    baseVersion: copyDefaults.baseVersion,
                    versionDescription: copyDefaults.versionDescription || undefined,
                    changeModule: copyDefaults.changeModule,
                    branchDpInfo: copyDefaults.branchDpInfo,
                    algorithmVersion: undefined,
                    branchCommitInput: undefined,
                    s3PackageUrl: undefined,
                  });
                } else if (next === 'precompiled') {
                  // snapshot 完整路径是 bos:// → 落到「S3 地址」子选项卡并带入 URL；
                  // 否则落到「版本号」子选项卡并回填原短版本号。
                  const full = (copyDefaults.fullPackageUrl || '').trim();
                  const isBos = /^bos:\/\//i.test(full);
                  setPrecompiledSubtype(isBos ? 's3' : 'version');
                  createForm.setFieldsValue({
                    algorithmVersion: isBos ? undefined : copyDefaults.algorithmVersion,
                    s3PackageUrl: isBos ? full : undefined,
                    branchCommitInput: undefined,
                  });
                }
              } else {
                createForm.setFieldsValue({
                  algorithmVersion: undefined,
                  branchCommitInput: undefined,
                  baseVersion: undefined,
                  changeModule: undefined,
                  branchDpInfo: undefined,
                  versionDescription: undefined,
                  s3PackageUrl: undefined,
                });
              }
            }}
          >
            {BUILD_TYPE_ORDER.map((bt) => (
              <Tooltip key={bt} title={BUILD_TYPE_TOOLTIPS[bt]} placement="top">
                <Radio
                  value={bt}
                  style={
                    bt === 'package_bazel'
                      ? { color: 'rgba(0, 0, 0, 0.35)' }
                      : undefined
                  }
                >
                  {BUILD_TYPE_LABELS[bt]}
                </Radio>
              </Tooltip>
            ))}
          </Radio.Group>
        </Form.Item>

        {buildType === 'precompiled' && (
          <>
            {/* 子选项卡：版本号 / S3 地址 / 使用 daily 版本 —— 提交时都走后端 package_neodata */}
            <Form.Item label="输入方式" tooltip={BUILD_TYPE_TOOLTIPS.precompiled}>
              <Segmented
                value={precompiledSubtype}
                onChange={(v) => {
                  const next = v as PrecompiledSubtype;
                  const prev = precompiledSubtype;
                  setPrecompiledSubtype(next);
                  // 切 subtype 时按目标 tab 清掉另外两侧的字段,避免残留误提交。
                  // 三 tab 都会用 algorithmVersion 字段承载版本号(daily tab 是选完
                  // tag 后由 DailyTagPicker.onPick 回填的),所以只有从 s3 切走时
                  // 需要清 s3PackageUrl;从其它 tab 切到 s3 时清 algorithmVersion。
                  // 从 daily → version 时保留已回填的 versionNo,让用户可以微调;
                  // 从 version → daily 时也保留 algorithmVersion,让原来的手动输入
                  // 值仍在 input 里显示,选新 tag 才会覆盖。
                  if (next === 's3') {
                    createForm.setFieldsValue({ algorithmVersion: undefined });
                  }
                  if (prev === 's3') {
                    createForm.setFieldsValue({ s3PackageUrl: undefined });
                  }
                  // 切离 daily 时清 tag / candidates 状态(下次进来重新选)。
                  if (prev === 'daily' && next !== 'daily') {
                    setPrecompiledDailyTagSelected(false);
                    setPrecompiledDailyCandidates([]);
                  }
                }}
                options={[
                  { label: PRECOMPILED_SUBTYPE_LABELS.version, value: 'version' },
                  { label: PRECOMPILED_SUBTYPE_LABELS.s3, value: 's3' },
                  { label: PRECOMPILED_SUBTYPE_LABELS.daily, value: 'daily' },
                ]}
              />
            </Form.Item>

            {precompiledSubtype === 'daily' && (
              <>
                <DailyTagPicker
                  onPick={(versionNo: string) => {
                    // 选 tag 后拉到该 tag 最新一条 versionNo,回填给 algorithmVersion。
                    // 与 buildType='daily' 分支的 onPick 语义一致(都是"tag 下最新"),
                    // 只是这里塞的是 algorithmVersion 而不是 baseVersion。
                    createForm.setFieldsValue({ algorithmVersion: versionNo });
                  }}
                  onVersionsLoaded={setPrecompiledDailyCandidates}
                  onTagChange={(tag) => {
                    setPrecompiledDailyTagSelected(!!tag);
                    // 清 tag 时同步清掉已回填的版本号,避免"回填还在生效"的错觉。
                    if (!tag) {
                      createForm.setFieldsValue({ algorithmVersion: undefined });
                    }
                  }}
                />
              </>
            )}

            {(precompiledSubtype === 'version' || precompiledSubtype === 'daily') && (
              <Form.Item
                name="algorithmVersion"
                label="版本号"
                rules={[{ required: true, message: '请输入版本号' }]}
                tooltip="填写已发布的算法包版本号（如 x86.20260430.release.10.8.3），后端按模板拼装 OBS 地址。"
                extra={renderProbeHint(algoVersionProbe)}
              >
                {precompiledSubtype === 'daily' ? (
                  <DailyBaseVersionPicker
                    candidates={precompiledDailyCandidates}
                    tagSelected={precompiledDailyTagSelected}
                    placeholder="选 Tag 后自动填入最新;点右侧查看历史或直接输入"
                    suffix={renderProbeSuffix(algoVersionProbe)}
                  />
                ) : (
                  <Input
                    placeholder="请输入版本号（如 x86.20260430.release.10.8.3）"
                    onBlur={(e) => handleAlgoVersionBlur(e.target.value)}
                    suffix={renderProbeSuffix(algoVersionProbe)}
                    allowClear
                  />
                )}
              </Form.Item>
            )}

            {precompiledSubtype === 's3' && (
              <Form.Item
                name="s3PackageUrl"
                label="S3 地址"
                tooltip="导入百度云的包，仅支持 bos:// 开头的地址。"
                rules={[
                  { required: true, message: '请输入 S3 地址' },
                  {
                    validator: (_, value) => {
                      const v = (value || '').trim();
                      if (!v) return Promise.resolve();
                      if (!/^bos:\/\//i.test(v)) {
                        return Promise.reject(new Error('S3 地址必须以 bos:// 开头（仅支持百度云）'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                extra={
                  <>
                    <span style={{ fontSize: 12, color: '#999' }}>导入百度云的包，仅支持 bos:// 开头的地址</span>
                    {renderProbeHint(s3PackageUrlProbe) && (
                      <>
                        <br />
                        {renderProbeHint(s3PackageUrlProbe)}
                      </>
                    )}
                  </>
                }
              >
                <Input
                  placeholder="bos://bucket/path/to/package.tgz"
                  suffix={renderProbeSuffix(s3PackageUrlProbe)}
                  allowClear
                />
              </Form.Item>
            )}
          </>
        )}

        {(buildType === 'package_bazel' || buildType === 'daily') && (
          <>
            {buildType === 'daily' && (
              <DailyTagPicker
                onPick={(baseVersion: string) => {
                  createForm.setFieldsValue({ baseVersion });
                }}
                onVersionsLoaded={setDailyBaseVersionCandidates}
                onTagChange={(tag) => {
                  setDailyTagSelected(!!tag);
                  // 清空 tag 时同步清掉表单里已回填的 baseVersion,不然用户会以为
                  // "回填还在生效",实际点提交也过必填校验但语义已经飘了。
                  if (!tag) {
                    createForm.setFieldsValue({ baseVersion: undefined });
                  }
                }}
              />
            )}
            {buildType === 'package_bazel' && (
              <Form.Item
                label="一键填版本"
                tooltip="选择任一预设，把对应版本的 neolix-repo / cyber / runtime / tools / dreamview / simulation 分支组合与默认基线版本合并到「分支信息」（同名仓库以预设为准、其余保留）；「自定义版本」不主动填充，但保留你已填的内容。预设由管理员在「系统 - 系统参数运维」页维护。"
              >
                <Select
                  value={bazelPresetChoice}
                  style={{ width: 200 }}
                  // 用 onSelect 而非 onChange：onChange 仅在值变化时触发，重复选同一预设不会有动作；
                  // onSelect 每次点选项都触发（含再次选中当前项），满足「选相同版本重新填充」。
                  onSelect={(v: 'custom' | string) => {
                    setBazelPresetChoice(v);
                    // 切到「自定义版本」时保留已填的基线/分支，作为手动微调的起点，不再清空
                    // （提交时 custom 仅表示不带 presetKey，字段仍从表单取）。
                    if (v !== 'custom') {
                      applyBazelPreset(v);
                    }
                  }}
                  options={[
                    { label: '自定义版本', value: 'custom' },
                    ...Object.entries(bazelPresets).map(([key, p]) => ({
                      label: p.label || key,
                      value: key,
                    })),
                  ]}
                />
                <span style={{ marginLeft: 8, fontSize: 12, color: '#9b9da3' }}>
                  选预设会合并到下方「分支信息」（同名仓库以预设为准、其余保留），并填入「基线版本」
                </span>
              </Form.Item>
            )}

            <Form.Item
              name="baseVersion"
              label="基线版本"
              rules={[
                // whitespace 让纯空格也算未填，一条规则即可覆盖「空」与「全空格」。
                // 曾经是 required + 一条同样报「请输入基线版本」的 validator，两条
                // 都命中就会在下方并排显示两行相同的报错。
                { required: true, whitespace: true, message: '请输入基线版本' },
              ]}
              tooltip="本次编译所基于的基线版本（base_version，必填）；编译产物版本号由后端按「日期.simulation.{环境}.{用户ID}.序号」自动生成。"
              extra={
                <>
                  <span style={{ color: '#9b9da3', fontSize: 12 }}>
                    必填，建议填写具体基线 tag（如 20260609.release.10.10.8）。
                  </span>
                  {renderProbeHint(baseVersionProbe) && (
                    <>
                      <br />
                      {renderProbeHint(baseVersionProbe)}
                    </>
                  )}
                </>
              }
            >
              {buildType === 'daily' ? (
                // daily 分支:主输入 + 右侧「历史」按钮弹表格。
                // 见 DailyBaseVersionPicker —— 替代掉了老的 AutoComplete 方案
                // (老方案会把当前 value 当过滤词,导致下拉里只看到自己)。
                <DailyBaseVersionPicker
                  candidates={dailyBaseVersionCandidates}
                  tagSelected={dailyTagSelected}
                  placeholder="选 Tag 后自动填入最新;点右侧查看历史或直接输入"
                  suffix={renderProbeSuffix(baseVersionProbe)}
                />
              ) : (
                <Input
                  placeholder="如 20260609.release.10.10.8"
                  suffix={renderProbeSuffix(baseVersionProbe)}
                  allowClear
                />
              )}
            </Form.Item>

            <Form.Item
              name="branchDpInfo"
              label="分支信息"
              extra={
                <span style={{ fontSize: 12, color: '#999' }}>
                  每行一对，格式：<code>module branch</code>（空格分隔）；
                  也兼容老格式 <code>module:branch</code>。提交时自动规整为
                  <code>module:branch,...</code> 发给后端。
                  <br />
                  分支位置也可直接填 <code>40 位 commitId</code>，
                  系统识别为 commit 直接使用、不再解析分支；<strong>短 commit 不支持</strong>。
                  <br />
                  仓库不变（无分支改动）则留空
                </span>
              }
              getValueFromEvent={(e) => {
                const v = e.target.value;
                createForm.setFieldsValue({ changeModule: deriveChangeModule(v) });
                return v;
              }}
            >
              <Input.TextArea
                placeholder={`planning orin_dev\ncontrol db31cf58ef237a576eab0d6e407b18fc80d29286`}
                autoSize={{ minRows: 2, maxRows: 8 }}
              />
            </Form.Item>

            {/* changeModule / versionDescription 不再展示为独立字段：
                - changeModule 由「分支信息」输入时的 deriveChangeModule 自动写入 form 值；
                - versionDescription 留空时 handleSubmit 会兜底按任务类型拼 "{taskType}仿真"。
                两个字段仍作为 Form 隐藏项存在，提交时 values.changeModule /
                values.versionDescription 正常读到，接口参数不变。 */}
            <Form.Item name="changeModule" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="versionDescription" hidden>
              <Input />
            </Form.Item>
          </>
        )}

        {/* 预测数据版本：仅 LPL（PNC 仿真）展示，位置紧跟版本号 / 基线版本。候选来自
            「预测数据版本列表」中开启「创建任务展示」的完整预测数据版本串
            （prediction.AD3.1.3），不再按 tag 拉全部内部版本。默认值来自系统参数
            （default / latest），用户留空时后端按同一口径兜底，故非必填。 */}
        {isLplTaskType && (
          <Form.Item
            name="predictionDataVersion"
            label="预测数据版本"
            tooltip={
              <div style={{ whiteSpace: 'pre-line' }}>
                {[
                  '非必填。默认值由「系统参数运维 → 创建任务默认预测数据版本」配置。',
                  'latest：该任务所属 dataVersion 下当前生效的预测数据版本（解析不出时兜底 orin_dev）；',
                  'default：使用 record 包内自带的原始预测；',
                  '也可选择「预测数据版本列表」中开启「创建任务展示」的预测数据版本（如 prediction.AD3.1.3）。',
                ].join('\n')}
              </div>
            }
          >
            <PredictionDataVersionPicker
              dataVersion={predictionDataVersion}
              defaultMode={predictionDefaultVersion}
              formEpoch={formEpoch}
            />
          </Form.Item>
        )}

        {perceptionRefreshTaskType && (
          <Form.Item
            name="perceptionTag"
            label="版本 tag"
            tooltip="版本号为 perception.{tag}.{序号}，每次提交分配新序号。留空时从算法版本解析 daily tag（如 release.10.12），解析不出用 orin_dev。"
            extra="输出路径：场景桶 perception/{版本号}/{pkl 文件名}/，目录名取场景 record_s3 末级目录（pkl 分片名），其次取 description，再其次取场景名"
            rules={[{
              pattern: /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/,
              message: '只允许字母、数字、点、下划线、中划线，最长 64',
            }]}
          >
            <Input placeholder="可选，如 release.10.12" allowClear />
          </Form.Item>
        )}

        {/* 模型主版本：与 Job 名称同级的顶层字段，渲染在其上方。这是决定本次仿真跑哪一版
            模型的关键参数，原先埋在「高级选项 · 自定义参数」里要连点三层才看得到，容易被漏掉。 */}
        <ModelReleaseField
          form={createForm}
          taskTypeId={selectedTaskTypeId}
          active={open}
          required={!!perceptionRefreshTaskType}
          definitions={customParameterDefinitions.definitions}
          loading={customParameterDefinitions.loading}
          error={customParameterDefinitions.error}
          onReload={customParameterDefinitions.reload}
          loadRelease={customParameterDefinitions.loadRelease}
        />

        <Form.Item
          name="jobName"
          label="Job 名称"
          rules={[{ required: true, message: '请输入 Job 名称' }]}
        >
          <Input placeholder="请输入 Job 名称" />
        </Form.Item>

        {/* 场景来源：按场景集合 / 按场景 / 按场景标签 三选一；「按任务查询」仅在
            有 byTaskQuery prop 时出现（从 JobTaskList「基于当前查询发起任务」进入）。
            「按场景标签」始终可用：无 presetSceneTagIds 时在弹窗内自选标签；
            有 preset（场景管理「标签检索」进入）时标签池锁定、只读展示。 */}
        <Form.Item label="场景来源" required>
          <Segmented
            value={sceneTab}
            onChange={(v) => setSceneTab(v as 'sceneSet' | 'scene' | 'byJob' | 'tag')}
            options={[
              { label: '按场景集合', value: 'sceneSet' },
              { label: '按场景', value: 'scene' },
              { label: '按场景标签', value: 'tag' },
              ...(byTaskQuery
                ? [{ label: '按任务查询', value: 'byJob' }]
                : []),
            ]}
          />
        </Form.Item>

        {/* 「按场景标签」页签：
            - 场景管理带入（presetSceneTagIds）：标签池锁定，只读展示可读路径；
            - 任务列表直接创建：弹窗内自选标签树（可多选；目录自动展开为子标签）。 */}
        {sceneTab === 'tag' && (
          presetSceneTagIds && presetSceneTagIds.length > 0 ? (
            <Form.Item label="所选标签（只读）">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {presetSceneTagIds.map((id) => (
                  <Tag key={id} color="processing">
                    {presetSceneTagLabels?.[id] || `标签 #${id}`}
                  </Tag>
                ))}
              </div>
              <div style={{ color: '#8b95a5', fontSize: 12, marginTop: 6 }}>
                提交后后端按所选标签实时取**交集**场景创建任务（不受单 Job 100 场景上限约束）。
              </div>
            </Form.Item>
          ) : (
            <Form.Item
              name="sceneTagIds"
              label="场景标签"
              rules={[{ required: true, message: '请选择至少一个场景标签' }]}
              extra="支持多选，多个标签取交集（目录仅作分组不可选）。提交后后端按所选标签实时取交集场景创建任务，不受单 Job 100 场景上限约束。"
            >
              <TreeSelect
                treeData={convertTagTreeToTreeData(tagTree)}
                placeholder="请选择场景标签（可多选）"
                treeCheckable
                showCheckedStrategy="SHOW_CHILD"
                showSearch
                filterTreeNode={(inputValue, treeNode) => {
                  const title = treeNode?.title as string;
                  return title?.toLowerCase().includes(inputValue.toLowerCase());
                }}
                treeDefaultExpandAll
                loading={tagTreeLoading}
                allowClear
                maxTagCount="responsive"
                style={{ width: '100%' }}
              />
            </Form.Item>
          )
        )}

        {/*
         * 场景管理带入的 daily 分组虚拟节点 chip 列表：TreeSelect 无对应 value 无法回显，
         * 单独在顶部渲染只读 tag；用户点 × 可删除。至少留一项（TreeSelect 或 chip 之一非空）。
         */}
        {sceneTab === 'sceneSet' && stagedGroupTokens.length > 0 && (
          <Form.Item label="从场景管理带入的分组">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {stagedGroupTokens.map((tok) => (
                <Tag
                  key={tok}
                  closable
                  color="processing"
                  onClose={() => setStagedGroupTokens(stagedGroupTokens.filter((t) => t !== tok))}
                >
                  {presetSceneSetGroupLabels?.[tok] || tok}
                </Tag>
              ))}
            </div>
          </Form.Item>
        )}

        {sceneTab === 'sceneSet' && (
          <Form.Item
            name="sceneSetIds"
            label="场景列表"
            required
            rules={[
              {
                required: true,
                validator: (_, value) => {
                  // stagedGroupTokens 非空视同已选（daily 分组虚拟节点带入，
                  // TreeSelect 不能显示无 value 的节点，两处任一非空都算通过）
                  const hasTree = Array.isArray(value) && value.length > 0;
                  const hasGroup = stagedGroupTokens.length > 0;
                  return hasTree || hasGroup
                    ? Promise.resolve()
                    : Promise.reject(new Error('请至少选择一个场景集合'));
                },
              },
            ]}
          >
            <TreeSelect
              treeData={convertSceneTreeToTreeData(sceneTree)}
              placeholder="请选择场景集合（可多选）"
              multiple
              treeCheckable
              showCheckedStrategy="SHOW_PARENT"
              showSearch
              filterTreeNode={(inputValue, treeNode) => {
                const title = treeNode?.title as string;
                return title?.toLowerCase().includes(inputValue.toLowerCase());
              }}
              style={{ width: '100%' }}
            />
          </Form.Item>
        )}

        {sceneTab === 'scene' && (
          <Form.Item
            name="sceneNames"
            label="场景名称"
            required
            rules={[
              {
                required: true,
                validator: (_, value) => {
                  const names = Array.isArray(value)
                    ? value.map((n: string) => (n || '').trim()).filter(Boolean)
                    : [];
                  if (names.length === 0) {
                    return Promise.reject(new Error('请输入场景名称'));
                  }
                  if (names.length > MAX_SCENE_IDS_PER_JOB) {
                    return Promise.reject(
                      new Error(`最多 ${MAX_SCENE_IDS_PER_JOB} 个场景，当前 ${names.length} 个`),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
            extra={
              <span style={{ fontSize: 12, color: '#999' }}>
                输入场景名称后回车确认，可输入多个（支持逗号 / 空格 / 换行分隔粘贴），
                上限 {MAX_SCENE_IDS_PER_JOB} 个
              </span>
            }
          >
            <Select
              mode="tags"
              open={false}
              tokenSeparators={[',', '，', ' ', ';', '；', '\n', '\t']}
              placeholder="请输入场景名称，如 LPL0000291017"
              style={{ width: '100%' }}
            />
          </Form.Item>
        )}

        {/* 按任务查询:场景池已由调用方冻结(源 jobId + filter 快照),弹窗内
            只做只读展示 + 修改筛选回退链接。想改 filter 需回 JobTaskList 页。 */}
        {sceneTab === 'byJob' && byTaskQuery && (
          <Form.Item
            label="场景池"
            extra={
              <a
                onClick={onClose}
                style={{ fontSize: 12, color: '#1677ff' }}
              >
                ← 修改筛选(回到 task 列表页重新查询)
              </a>
            }
          >
            <div
              style={{
                padding: '10px 12px',
                background: '#fafafa',
                border: '1px solid #f0f0f0',
                borderRadius: 6,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                fontSize: 13,
              }}
            >
              <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
                <span style={{ color: '#8c8c8c' }}>源 Job</span>
                <span style={{
                  fontFamily: '"JetBrains Mono", "Fira Code", "SF Mono", Menlo, Consolas, monospace',
                  color: '#1f1f1f',
                }}>
                  #{byTaskQuery.jobId}
                </span>
                {byTaskQuery.jobName ? (
                  <span style={{
                    color: '#595959',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {byTaskQuery.jobName}
                  </span>
                ) : null}
                {typeof byTaskQuery.sceneCount === 'number' ? (
                  <span style={{ marginLeft: 'auto' }}>
                    <span style={{ color: '#8c8c8c', marginRight: 4 }}>将发起</span>
                    <strong style={{
                      color: '#1677ff',
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {byTaskQuery.sceneCount}
                    </strong>
                    <span style={{ color: '#8c8c8c', marginLeft: 4 }}>个场景</span>
                  </span>
                ) : null}
              </div>
              {byTaskQuery.filterEntries && byTaskQuery.filterEntries.length > 0 ? (
                <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                  {byTaskQuery.filterEntries.map((f) => (
                    <li
                      key={f.key}
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 12,
                        padding: '4px 0',
                        fontSize: 12,
                      }}
                    >
                      <span style={{ color: '#8c8c8c', minWidth: 96, flexShrink: 0 }}>
                        {f.label}
                      </span>
                      <span style={{ color: '#1f1f1f', flex: 1, wordBreak: 'break-all' }}>
                        {f.valueLabel}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div style={{ color: '#bfbfbf', fontSize: 12 }}>
                  未应用任何筛选 —— 会用该 Job 的全部场景发起任务
                </div>
              )}
            </div>
          </Form.Item>
        )}

        {/* 高级选项：默认收起。自定义参数必须提前挂载，让复制值与提交校验在收起时也生效。
            选择 Collapse ghost 让面板与 Form 视觉不脱节，避免在弹窗内出现重边框感。 */}
        <Collapse
          ghost
          size="small"
          activeKey={advancedOpen ? ['advanced'] : []}
          onChange={keys => setAdvancedOpen(keys.includes('advanced'))}
          items={[{
            key: 'advanced',
            forceRender: true,
            label: <span style={{ fontSize: 13, color: '#5e6ad2' }}>高级选项</span>,
            children: (
              <>
                <CustomParametersFields
                  form={createForm}
                  taskTypeId={selectedTaskTypeId}
                  active={open}
                  definitions={customParameterDefinitions.definitions}
                  loading={customParameterDefinitions.loading}
                  error={customParameterDefinitions.error}
                  onReload={customParameterDefinitions.reload}
                  promotedKeys={PROMOTED_CUSTOM_PARAMETER_KEYS}
                />
                <Form.Item
                  name="priority"
                  label="优先级"
                  initialValue={6}
                  tooltip="调度优先级。默认「中」;高/紧急建议少用,避免占用他人配额。"
                  style={{ marginBottom: 16 }}
                >
                  <Select
                    style={{ width: 200 }}
                    options={priorityList.map((p) => ({
                      label: p.label ?? p.name ?? String(p.value),
                      value: Number(p.value ?? p.code),
                    }))}
                  />
                </Form.Item>

                <Form.Item
                  name="repeatTimes"
                  label="重复运行次数"
                  initialValue={1}
                  tooltip="同一份参数循环创建 N 个 Job（1-10）；N>1 时 jobName 自动追加 _#i/N 序号方便检索同一批次。"
                  rules={[{ type: 'integer', min: 1, max: 10, message: '请输入 1-10 之间的整数' }]}
                  style={{ marginBottom: 16 }}
                >
                  <InputNumber min={1} max={10} precision={0} style={{ width: 120 }} />
                </Form.Item>

                {/* 开启定时/延迟/循环创建 Job（docs/schedule-v3.md §5.1）。
                    开关开后上方 Alert 提示「不走同步 preflight」；三选一 radio 切换触发方式。 */}
                <Form.Item label="开启定时" tooltip="开关开后本次提交将不立即建 Job，而是写一条定时记录，到点由后台协程同进程 await 对应异步 service 方法。分支/包错误到点才会暴露，请自行单次触发确保参数正确。">
                  <Space>
                    <Switch
                      checked={enableSchedule}
                      onChange={(checked) => {
                        setEnableSchedule(checked);
                        // 关闭时清掉残留的定时字段值，避免下次开启时旧值闪显
                        if (!checked) {
                          createForm.setFieldsValue({
                            runAt: undefined,
                            delayValue: undefined,
                            delayUnit: 'minutes',
                            cronExpr: undefined,
                          });
                          setCronPreview(null);
                        } else if (!createForm.getFieldValue('delayUnit')) {
                          // 默认单位 minutes
                          createForm.setFieldsValue({ delayUnit: 'minutes' });
                        }
                      }}
                    />
                    <span style={{ color: '#9b9da3', fontSize: 12 }}>
                      {enableSchedule ? '本次将不立即建 Job，到点自动触发' : '默认关闭，立即建 Job'}
                    </span>
                  </Space>
                </Form.Item>

                {enableSchedule && (
                  <Form.Item label="触发方式" required>
                    <Radio.Group
                      value={scheduleMode}
                      onChange={(e) => {
                        const next = e.target.value as 'scheduled' | 'delayed' | 'cron';
                        setScheduleMode(next);
                        // 切换 mode 时清掉另一侧字段，避免残留误提交
                        createForm.setFieldsValue({
                          runAt: undefined,
                          delayValue: undefined,
                          delayUnit: 'minutes',
                          cronExpr: undefined,
                        });
                        setCronPreview(null);
                      }}
                    >
                      <Radio value="scheduled">定时</Radio>
                      <Radio value="delayed">延时</Radio>
                      <Radio value="cron">循环 (cron)</Radio>
                    </Radio.Group>
                  </Form.Item>
                )}

                {enableSchedule && scheduleMode === 'scheduled' && (
                  <Form.Item
                    name="runAt"
                    label="定时触发时间"
                    rules={[
                      { required: true, message: '请选择触发时间' },
                      {
                        validator: (_, value: Dayjs | undefined) => {
                          if (!value) return Promise.resolve();
                          if (value.toDate().getTime() < Date.now() + 60_000) {
                            return Promise.reject(new Error('触发时间不得早于当前时间 + 1 分钟'));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                    extra={
                      <span style={{ fontSize: 12, color: '#999' }}>
                        到点后自动建 Job；时区按本地浏览器处理。
                      </span>
                    }
                  >
                    <DatePicker
                      showTime
                      format="YYYY-MM-DD HH:mm"
                      style={{ width: '100%' }}
                      placeholder="选择触发时间"
                      disabledDate={(current) => {
                        // 不允许选过去日期
                        return current ? current.endOf('day') < dayjs().startOf('day') : false;
                      }}
                    />
                  </Form.Item>
                )}

                {enableSchedule && scheduleMode === 'delayed' && (
                  <Form.Item label="延时触发" required>
                    <Space.Compact style={{ width: '100%' }}>
                      <Form.Item name="delayValue" noStyle rules={[{ required: true, message: '请输入延时数值' }]}>
                        <InputNumber min={1} precision={0} placeholder="数值" style={{ width: '60%' }} />
                      </Form.Item>
                      <Form.Item name="delayUnit" noStyle initialValue="minutes">
                        <Select style={{ width: '40%' }} options={[
                          { label: '分钟', value: 'minutes' },
                          { label: '小时', value: 'hours' },
                          { label: '天', value: 'days' },
                        ]} />
                      </Form.Item>
                    </Space.Compact>
                  </Form.Item>
                )}

                {enableSchedule && scheduleMode === 'cron' && (
                  <Form.Item
                    name="cronExpr"
                    label="Cron 表达式"
                    rules={[
                      { required: true, message: '请输入 cron 表达式' },
                    ]}
                    extra={
                      <span style={{ fontSize: 12, color: '#999' }}>
                        5 段标准 cron（分 时 日 月 周）；最小间隔 ≥5 分钟。例如 <code>0 9 * * 1</code> = 每周一 09:00。
                      </span>
                    }
                  >
                    <Input placeholder="0 9 * * 1" />
                  </Form.Item>
                )}

                {enableSchedule && scheduleMode === 'cron' && (
                  <div style={{ marginBottom: 16, marginTop: -8 }}>
                    <Space>
                      <Button
                        size="small"
                        onClick={async () => {
                          const expr = (createForm.getFieldValue('cronExpr') || '').toString().trim();
                          if (!expr) {
                            message.warning('请先填写 cron 表达式');
                            return;
                          }
                          setCronPreviewLoading(true);
                          try {
                            const r = await previewCron(expr, 'Asia/Shanghai', 3);
                            setCronPreview(r.times);
                          } catch (err) {
                            console.error('cron 预演失败:', err);
                          } finally {
                            setCronPreviewLoading(false);
                          }
                        }}
                        loading={cronPreviewLoading}
                      >
                        预演未来 3 次
                      </Button>
                      {cronPreview && cronPreview.length > 0 && (
                        <span style={{ fontSize: 12, color: '#16a34a' }}>
                          下次触发：{dayjs(cronPreview[0]).format('YYYY-MM-DD HH:mm')}；
                          之后：{cronPreview.slice(1).map(t => dayjs(t).format('MM-DD HH:mm')).join(' / ')}
                        </span>
                      )}
                    </Space>
                  </div>
                )}
              </>
            ),
          }]}
        />
      </Form>
    </Modal>
  );
}
